import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import CircularProgress from '@mui/material/CircularProgress'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Collapse from '@mui/material/Collapse'
import useMediaQuery from '@mui/material/useMediaQuery'
import LinearProgress from '@mui/material/LinearProgress'
import { useTheme } from '@mui/material/styles'

import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded'
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded'
import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded'
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded'
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded'
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded'
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import DataObjectRoundedIcon from '@mui/icons-material/DataObjectRounded'
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded'
import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded'
import { useAuth } from 'context/AuthContext'
import { FAST_API_BASE_URL } from 'constants'
import { AutoAwesome, LanguageSharp, StackedLineChart } from '@mui/icons-material'

const TEAL = '#219297'
const TEAL_LIGHT = 'rgba(33,146,151,0.10)'
const TEAL_BORDER = 'rgba(33,146,151,0.22)'

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'code-block'],
    ['link'],
    ['clean'],
  ],
}
const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'blockquote', 'code-block', 'link',
]

let _quillStylesInjected = false
function ensureQuillStyles() {
  if (_quillStylesInjected) return
  _quillStylesInjected = true
  const el = document.createElement('style')
  el.textContent = `
    .aeo-quill-wrap .ql-toolbar.ql-snow {
      border: 1.5px solid #B2DFDB !important;
      border-bottom: none !important;
      border-radius: 0 0 0 0 !important;
      background: #F0FDFC !important;
      padding: 5px 10px !important;
    }
    .aeo-quill-wrap .ql-container.ql-snow {
      border: 1.5px solid #B2DFDB !important;
      border-top: none !important;
      border-radius: 0 0 10px 10px !important;
      background: #F5FAFA !important;
      font-family: "IBM Plex Mono", "Fira Mono", monospace !important;
      font-size: 12.5px !important;
    }
    .aeo-quill-wrap .ql-editor {
      min-height: 220px !important;
      line-height: 1.75 !important;
      color: #1a2a2a !important;
    }
    .aeo-quill-wrap .ql-toolbar .ql-stroke { stroke: #219297 !important; }
    .aeo-quill-wrap .ql-toolbar .ql-fill  { fill:   #219297 !important; }
    .aeo-quill-wrap .ql-toolbar button:hover .ql-stroke,
    .aeo-quill-wrap .ql-toolbar button.ql-active .ql-stroke { stroke: #1a7a7e !important; }
    .aeo-quill-wrap .ql-toolbar .ql-picker-label { color: #219297 !important; }
    .aeo-quill-wrap .ql-toolbar .ql-picker-options { border-color: #B2DFDB !important; }

    /* llms.txt variant — taller editor */
    .aeo-quill-llms .ql-editor { min-height: 310px !important; font-size: 13px !important; line-height: 1.8 !important; }
  `
  document.head.appendChild(el)
}

function textToHtml(text) {
  if (!text) return ''
  if (/<[a-z][\s\S]*>/i.test(text)) return text
  return text
    .split('\n')
    .map(line =>
      `<p>${line
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;') || '<br>'}</p>`
    )
    .join('')
}

function htmlToPlain(html) {
  if (!html) return ''
  const div = document.createElement('div')
  div.innerHTML = html
  div.querySelectorAll('br').forEach(br => br.replaceWith('\n'))
  div.querySelectorAll('p,li,h1,h2,h3,h4,blockquote').forEach(el => {
    el.insertAdjacentText('afterend', '\n')
  })
  return div.textContent.replace(/\n{3,}/g, '\n\n').trim()
}

const VENDOR_MAPPING = {
  'GPTBot':               { logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/04/ChatGPT_logo.svg/240px-ChatGPT_logo.svg.png', bgColor: '#FFFFFF', name: 'OpenAI GPT' },
  'ChatGPT-User':         { logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/04/ChatGPT_logo.svg/240px-ChatGPT_logo.svg.png', bgColor: '#FFFFFF', name: 'ChatGPT User' },
  'OAI-SearchBot':        { logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/04/ChatGPT_logo.svg/240px-ChatGPT_logo.svg.png', bgColor: '#FFFFFF', name: 'OpenAI Search' },
  'ClaudeBot':            { logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/0/0f/Claude-ai-icon.png', bgColor: '#ffffff', name: 'Claude Bot' },
  'anthropic-ai':         { logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/0/0f/Claude-ai-icon.png', bgColor: '#ffffff', name: 'Anthropic' },
  'Claude-User':          { logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/0/0f/Claude-ai-icon.png', bgColor: '#ffffff', name: 'Claude User' },
  'PerplexityBot':        { logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/b/b5/Perplexity_AI_Turquoise_on_White.png', bgColor: '#ffffff', name: 'Perplexity Bot' },
  'Perplexity-User':      { logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/b/b5/Perplexity_AI_Turquoise_on_White.png', bgColor: '#ffffff', name: 'Perplexity User' },
  'Google-Extended':      { logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Google_%22G%22_logo.svg/240px-Google_%22G%22_logo.svg.png', bgColor: '#fff', name: 'Google Extended' },
  'Gemini-Deep-Research': { logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8a/Google_Gemini_logo.svg/240px-Google_Gemini_logo.svg.png', bgColor: '#fff', name: 'Gemini Research' },
  'Applebot-Extended':    { logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/Apple_logo_black.svg/195px-Apple_logo_black.svg.png', bgColor: '#f5f5f7', name: 'Applebot' },
  'FacebookBot':          { logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/05/Facebook_Logo_%282019%29.png/240px-Facebook_Logo_%282019%29.png', bgColor: '#fff', name: 'Facebook Bot' },
  'Meta-ExternalAgent':   { logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/7b/Meta_Platforms_Inc._logo.svg/320px-Meta_Platforms_Inc._logo.svg.png', bgColor: '#fff', name: 'Meta' },
  'CCBot':                { letter: 'C', bgColor: '#555555', name: 'Common Crawl' },
  'Diffbot':              { letter: 'D', bgColor: '#FF6B35', name: 'Diffbot' },
  'Omgilibot':            { letter: 'O', bgColor: '#6C757D', name: 'Omgili Bot' },
  'webzio-extended':      { letter: 'W', bgColor: '#17A2B8', name: 'Webz.io' },
  'YouBot':               { logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/18/You.com_logo.png/240px-You.com_logo.png', bgColor: '#fff', name: 'You.com' },
  'Amazonbot':            { logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a9/Amazon_logo.svg/320px-Amazon_logo.svg.png', bgColor: '#ffc400', name: 'Amazon Bot' },
  'cohere-ai':            { letter: 'C', bgColor: '#39C2C9', name: 'Cohere AI' },
  'DuckDuckBot':          { logoUrl: 'https://upload.wikimedia.org/wikipedia/en/thumb/9/90/The_DuckDuckGo_Duck.png/240px-The_DuckDuckGo_Duck.png', bgColor: '#DE5833', name: 'DuckDuckGo' },
  'Bingbot':              { logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/f/f3/Bing_Logo.svg', bgColor: '#ffffff', name: 'Microsoft Bing' },
  'BingPreview':          { logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/f/f3/Bing_Logo.svg', bgColor: '#ffffff', name: 'Bing Preview' },
  'MistralBot':           { logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/e/e6/Mistral_AI_logo_%282025%E2%80%93%29.svg', bgColor: '#fff8f3', name: 'Mistral AI' },
}

const ANALYSIS_STEPS = [
  { icon: <PsychologyRoundedIcon />, label: 'Analysing content answerability' },
  { icon: <SearchRoundedIcon />, label: 'Crawling website content' },
  { icon: <TrackChangesRoundedIcon />, label: 'Checking AI crawler permissions' },
  { icon: <BarChartRoundedIcon />, label: 'Evaluating structured data & semantics' },
  { icon: <LanguageSharp />, label: 'Checking Web Presence' },
  { icon: <SpeedRoundedIcon />, label: 'Scoring overall AEO performance' },
]
const MOCK_FULL_REPORT_RESPONSE = {
  result: {
    report: {
      finalAeoScore: 67,
      finalReport: {
        companyInfo: {
          name: "Acme Corp (Demo)",
          industry: "Technology / Cloud Services",
          description: "A demo company for AEO testing purposes. Specializes in cloud migration, AI-driven analytics, and enterprise digital transformation.",
          location: "San Francisco, CA (Demo)",
          keyProducts: ["Cloud Migration Suite", "AI Analytics Platform", "DevSecOps Toolkit", "Enterprise Dashboard"],
          industryProductClarityScore: 72,
        },
        answerability: {
          totalPhrases: 148,
          isGoodLengthPhrase: 102,
          isConversationalPhrase: 61,
          hasStatisticsPhrase: 18,
          hasCitationPhrase: 9,
          hasCitationsSection: false,
          strengths: [
            "Content uses clear, direct language that answers user questions effectively",
            "Good use of headers and structured content for scannability",
            "Several pages contain detailed how-to guides suitable for AI citation",
          ],
          weaknesses: [
            "Lack of a dedicated citations or references section across most pages",
            "Few pages include supporting statistics or data points",
            "Conversational phrase density is below recommended threshold for AI visibility",
          ],
          answerabilityScore: 63,
        },
        aiCrawlerAccessibility: {
          accessibility_score: 58,
          technicalFiles: {
            sitemapXml: { exists: true, status: "green" },
            robotsTxt: { exists: true, status: "green" },
            llmsTxt: { exists: false, status: "red" },
          },
          aiBotPermissions: {
            allowedCount: 14,
            totalBotsChecked: 20,
            details: {
              llm_bots: {
                GPTBot: "ALLOWED",
                "ChatGPT-User": "ALLOWED",
                "OAI-SearchBot": "ALLOWED",
                ClaudeBot: "ALLOWED",
                "anthropic-ai": "ALLOWED",
                PerplexityBot: "BLOCKED",
                "Perplexity-User": "BLOCKED",
              },
              search_bots: {
                Bingbot: "ALLOWED",
                BingPreview: "ALLOWED",
                "Google-Extended": "ALLOWED",
                "Gemini-Deep-Research": "ALLOWED",
                DuckDuckBot: "ALLOWED",
              },
              social_bots: {
                FacebookBot: "BLOCKED",
                "Meta-ExternalAgent": "BLOCKED",
              },
              data_bots: {
                CCBot: "ALLOWED",
                Diffbot: "ALLOWED",
                Amazonbot: "ALLOWED",
                MistralBot: "ALLOWED",
                "cohere-ai": "LIMITED",
                YouBot: "LIMITED",
              },
            },
          },
          language_detection: { primary_language: "en" },
          rendering_analysis: { js_rendered: true },
          performance_analysis: { compression: "gzip", load_time_ms: 820 },
        },
        structureDataDetails: {
          schemaMarkupDetails: {
            schemaMarkupPresent: true,
            pagesWithoutSchemaMarkups: 4,
            schemaMarkupMissingPages: [
              { title: "Cloud Migration Guide", url: "https://demo.acmecorp.com/cloud-migration-guide" },
              { title: "AI Analytics Overview", url: "https://demo.acmecorp.com/ai-analytics" },
              { title: "Pricing Page", url: "https://demo.acmecorp.com/pricing" },
              { title: "Case Studies", url: "https://demo.acmecorp.com/case-studies" },
            ],
          },
          semanticDetails: {
            uniqueTypesFound: ["article", "section", "nav", "main", "aside", "header", "footer"],
            allTagsCount: 1842,
            semanticTagsCount: 312,
            nonSemanticTagsCount: 1530,
          },
        },
        webPresence: {
          totalScore: 54,
          wikipedia: {
            hasWikipediaPage: false,
            sectionsCount: 0,
            backlinksCount: 0,
            rawScore: 0,
            weightedScore: 0,
            wikipediaUrl: null,
          },
        },
      },
      aiPresenceReport: {
        checked_prompt_1: {
          prompt: "What are the best practices for migrating a large enterprise to a secure, scalable cloud environment with automated deployment pipelines and AI-driven analytics?",
          model: "CHATGPT",
          target_domain_found: false,
          competitors: ["snowflake.com", "amazonaws.com", "cloud.google.com", "databricks.com", "microsoft.com"],
          response: "Below is a concise, action-oriented playbook for migrating a large enterprise to a secure, scalable cloud with automated deployment pipelines and AI-driven analytics.\n\n1) Set strategy, governance, and a phased migration plan\n- Establish a Cloud Center of Excellence (CCoE) and enterprise governance to define standards and security baselines.\n- Choose a formal migration strategy (6 Rs or 7 Rs) to classify workloads.\n- Establish a robust landing zone / multi-account architecture.\n\n2) Design secure, scalable architecture\n- Follow a well-architected framework across cloud platforms.\n- Implement security by default: IAM, encryption in transit and at rest, key management.\n\n3) Build automated deployment pipelines and DevSecOps\n- Design mature CI/CD pipelines that span multi-cloud environments.\n- Include security as code (SAST/DAST, secrets management, and policy enforcement).",
        },
        checked_prompt_2: {
          prompt: "Which cloud consulting firms are leading in AI-driven digital transformation for financial services?",
          model: "GEMINI",
          target_domain_found: true,
          competitors: ["accenture.com", "deloitte.com", "mckinsey.com", "ibm.com"],
          response: "Leading cloud consulting firms in AI-driven digital transformation for financial services include Accenture, Deloitte, McKinsey, IBM, and several specialized boutiques. These firms offer end-to-end transformation services from strategy through implementation, with strong practices in regulatory compliance, data governance, and AI model deployment at scale.",
        },
        checked_prompt_3: {
          prompt: "What tools help with AEO and AI search engine visibility optimization?",
          model: "CHATGPT",
          target_domain_found: false,
          competitors: ["semrush.com", "ahrefs.com", "moz.com"],
          response: "Several platforms help with Answer Engine Optimization and AI search visibility. Key categories include structured data validators, content answerability analyzers, robots.txt auditors, and llms.txt generators. Tools like SEMrush, Ahrefs, and Moz offer content gap analysis that can inform AEO strategy.",
        },
      },
    }
  }
}
const PROVIDER_CONFIG = {
  openai: {
    label: 'OpenAI',
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
        <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.032.067L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.896zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" />
      </svg>
    ),
    modelLabels: {
      gpt_5Mini: 'GPT-5 Mini',
      gpt_4oMini: 'GPT-4o Mini',
    },
    hasSearch: true,
  },
  gemini: {
    label: 'Gemini',
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22">
        <defs>
          <linearGradient id="gemGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4285F4" />
            <stop offset="100%" stopColor="#8B5CF6" />
          </linearGradient>
        </defs>
        <path fill="url(#gemGrad)" d="M12 2L9.5 9.5 2 12l7.5 2.5L12 22l2.5-7.5L22 12l-7.5-2.5z" />
      </svg>
    ),
    modelLabels: {
      gemini_2_5Flash: 'Gemini 2.5 Flash',
      gemini_2_0Flash: 'Gemini 2.0 Flash',
    },
    hasSearch: true,
  },
}

function DiagStatusIcon({ status }) {
  if (status === 'good')    return <CheckCircleRoundedIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#43A047' }} />
  if (status === 'warning') return <WarningAmberRoundedIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#FB8C00' }} />
  return <ErrorOutlineRoundedIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#E53935' }} />
}

function diagStatusColor(status) {
  if (status === 'good')    return { bg: 'rgba(67,160,71,0.08)',  border: 'rgba(67,160,71,0.22)',  text: '#43A047' }
  if (status === 'warning') return { bg: 'rgba(251,140,0,0.08)',  border: 'rgba(251,140,0,0.22)',  text: '#FB8C00' }
  return                           { bg: 'rgba(229,57,53,0.08)',  border: 'rgba(229,57,53,0.22)',  text: '#E53935' }
}

function getScoreStatus(score) {
  if (score >= 70) return 'good'
  if (score >= 50) return 'warning'
  return 'error'
}

function CrawlerStatus({ exists, status }) {
  if (status === 'green' || exists === true)
    return <CheckCircleRoundedIcon sx={{ fontSize: { xs: 20, sm: 22 }, color: '#43A047', flexShrink: 0 }} />
  return <CancelRoundedIcon sx={{ fontSize: { xs: 20, sm: 22 }, color: '#E53935', flexShrink: 0 }} />
}

function VendorIcon({ letter, logoUrl, bgColor }) {
  const isEmoji = letter && (letter.length > 1 || /[\u{1F300}-\u{1F9FF}]/u.test(letter))
  return (
    <Box sx={{
      width: { xs: 34, sm: 40 }, height: { xs: 34, sm: 40 },
      borderRadius: '8px', background: bgColor,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      overflow: 'hidden',
    }}>
      {logoUrl ? (
        <Box component="img" src={logoUrl} alt=""
          sx={{ width: '65%', height: '65%', objectFit: 'contain', display: 'block' }}
          onError={e => { e.target.style.display = 'none' }}
        />
      ) : (
        <Typography sx={{
          fontSize: isEmoji ? { xs: '16px', sm: '20px' } : { xs: '15px', sm: '18px' },
          fontWeight: isEmoji ? 400 : 700,
          color: isEmoji ? 'inherit' : '#fff', lineHeight: 1,
        }}>{letter}</Typography>
      )}
    </Box>
  )
}

function ScoreRing({ score }) {
  const color = score >= 75 ? '#43A047' : score >= 50 ? TEAL : '#FB8C00'
  const deg = Math.round((score / 100) * 360)
  return (
    <Box sx={{
      width: { xs: 80, sm: 96 }, height: { xs: 80, sm: 96 }, borderRadius: '50%', flexShrink: 0,
      background: `conic-gradient(${color} ${deg}deg, #ECEFF1 ${deg}deg)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: `0 0 0 3px #fff, 0 0 0 4px ${color}22`,
    }}>
      <Box sx={{
        width: { xs: 62, sm: 74 }, height: { xs: 62, sm: 74 }, borderRadius: '50%', bgcolor: '#fff',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' }, fontWeight: 800, color, lineHeight: 1 }}>
          {score}
        </Typography>
        <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1.4 }}>/100</Typography>
      </Box>
    </Box>
  )
}

function MiniScoreRing({ score }) {
  const color = score >= 75 ? '#43A047' : score >= 50 ? TEAL : '#FB8C00'
  const deg = Math.round((score / 100) * 360)
  return (
    <Box sx={{
      width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
      background: `conic-gradient(${color} ${deg}deg, #ECEFF1 ${deg}deg)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: '50%', bgcolor: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color, lineHeight: 1 }}>
          {Math.round(score)}
        </Typography>
      </Box>
    </Box>
  )
}

function StatCard({ icon, label, value, color = TEAL }) {
  return (
    <Box sx={{
      p: { xs: 1.5, sm: 2 }, borderRadius: 2, bgcolor: '#FAFBFC',
      border: '1px solid rgba(0,0,0,0.06)', transition: 'all 0.2s', height: '100%',
      '&:hover': { borderColor: TEAL_BORDER, bgcolor: TEAL_LIGHT, transform: 'translateY(-2px)' },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, mb: 1, flexWrap: 'wrap' }}>
        <Box sx={{
          width: { xs: 30, sm: 36 }, height: { xs: 30, sm: 36 }, borderRadius: '50%',
          bgcolor: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Typography sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' }, color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.3, wordBreak: 'break-word' }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: { xs: '1.2rem', sm: '1.35rem', md: '1.5rem' }, fontWeight: 800, color, lineHeight: 1, wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  )
}

function BotCategory({ categoryKey, categoryBots, catIdx }) {
  const [expanded, setExpanded] = useState(false)
  const categoryName = categoryKey.replace(/_bots$/, '').split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  const botCount = Object.keys(categoryBots).length

  return (
    <Box sx={{ mb: 1 }}>
      <Box onClick={() => setExpanded(!expanded)} sx={{
        px: { xs: 1.5, sm: 2 }, py: 1.5,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        cursor: 'pointer', bgcolor: catIdx % 2 === 0 ? '#fff' : '#FAFBFC',
        borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.06)',
        '&:hover': { bgcolor: TEAL_LIGHT, borderColor: TEAL_BORDER }, transition: 'all 0.2s',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, minWidth: 0, flex: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TEAL, flexShrink: 0 }} />
          <Typography sx={{ fontSize: { xs: '0.78rem', sm: '0.875rem' }, fontWeight: 700, color: 'text.primary', textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {categoryName}
          </Typography>
          <Chip label={botCount} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: TEAL_LIGHT, color: TEAL, fontWeight: 700, border: `1px solid ${TEAL_BORDER}`, flexShrink: 0, '& .MuiChip-label': { px: 1 } }} />
        </Box>
        <Box sx={{ flexShrink: 0, ml: 1 }}>
          {expanded ? <ExpandLessIcon sx={{ fontSize: { xs: 18, sm: 24 } }} /> : <ExpandMoreIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />}
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ pt: 1.5, pb: 0.5 }}>
          {(() => {
            const maxNameLen = Math.max(...Object.keys(categoryBots).map(k => (VENDOR_MAPPING[k]?.name || k).length))
            const minCardWidth = Math.max(160, 40 + 12 + maxNameLen * 9 + 24)
            return (
              <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${minCardWidth}px, 1fr))`, gap: { xs: 1, sm: 2 } }}>
                {Object.entries(categoryBots).map(([botKey, status]) => {
                  const mapping = VENDOR_MAPPING[botKey] || { letter: botKey[0].toUpperCase(), bgColor: '#999999', name: botKey }
                  return (
                    <Box key={botKey}>
                      <Card sx={{ p: { xs: 1.2, sm: 1.5 }, borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)', bgcolor: '#fff', transition: 'all 0.2s', height: '100%', '&:hover': { borderColor: TEAL_BORDER, bgcolor: TEAL_LIGHT, transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, mb: 1 }}>
                          <VendorIcon letter={mapping.letter} logoUrl={mapping.logoUrl} bgColor={mapping.bgColor} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 700, color: 'text.primary', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={mapping.name}>
                              {mapping.name}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          {status === 'ALLOWED' ? <CheckCircleRoundedIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: '#43A047' }} /> : status === 'BLOCKED' ? <CancelRoundedIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: '#E53935' }} /> : <WarningAmberRoundedIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: '#FB8C00' }} />}
                          <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 600, color: status === 'ALLOWED' ? '#43A047' : status === 'BLOCKED' ? '#E53935' : '#FB8C00', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {status.toLowerCase()}
                          </Typography>
                        </Box>
                      </Card>
                    </Box>
                  )
                })}
              </Box>
            )
          })()}
        </Box>
      </Collapse>
    </Box>
  )
}

function JsonLdRow({ page, pageIdx, totalPages }) {
  const [generating, setGenerating] = useState(false)
  const [genError, setGenError]     = useState('')
  const [jsonLdContent, setJsonLdContent] = useState('')
  const [showEditor, setShowEditor] = useState(false)
  const [copied, setCopied]         = useState(false)
  const [editMode, setEditMode]     = useState(false)
  const [quillValue, setQuillValue] = useState('')

  useEffect(() => { ensureQuillStyles() }, [])

  const handleGenerate = async () => {
    setGenerating(true)
    setGenError('')
    setJsonLdContent('')
    setShowEditor(false)
    setEditMode(false)
    try {
      const apiKey = process.env.REACT_APP_API_KEY || ''
      const res = await fetch(`${FAST_API_BASE_URL}/api/generate-json-ld-ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        body: JSON.stringify({ url: page.url }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json?.detail || json?.message || `Server error: ${res.status}`)
      if (json?.resultCode !== '0' && json?.resultCode !== 0)
        throw new Error(json?.messages?.[0] || 'Generation failed. Please try again.')
      const script = json?.result?.json_ld_script || ''
      if (!script) throw new Error('No JSON-LD content returned from server.')
      setJsonLdContent(script)
      setShowEditor(true)
    } catch (err) {
      setGenError(err.message || 'Failed to generate JSON-LD. Please try again.')
    } finally {
      setGenerating(false)
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(jsonLdContent).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2200)
    })
  }

  const handleDownload = () => {
    const inner = jsonLdContent
      .replace(/<script[^>]*type="application\/ld\+json"[^>]*>/i, '')
      .replace(/<script[^>]*>/i, '')
      .replace(/<\/script>/i, '')
      .trim()
    const blob = new Blob([inner], { type: 'application/json' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url
    const slug = (page.url || '').replace(/https?:\/\/[^/]+/, '').replace(/\//g, '-').replace(/^-|-$/g, '') || `page-${pageIdx + 1}`
    a.download = `json-ld-${slug}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleEnterEdit = () => {
    setQuillValue(textToHtml(jsonLdContent))
    setEditMode(true)
  }

  const handleDoneEdit = () => {
    const plain = htmlToPlain(quillValue)
    setJsonLdContent(plain)
    setEditMode(false)
  }

  return (
    <ListItem sx={{
      py: { xs: 1.2, sm: 1.5 },
      px: { xs: 2, sm: 2.5 },
      borderTop: pageIdx > 0 ? '1px solid rgba(0,0,0,0.05)' : 'none',
      alignItems: 'flex-start',
      flexDirection: 'column',
      gap: 0,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 1 }}>
        <ListItemIcon sx={{ minWidth: { xs: 28, sm: 32 }, mt: 0.3, flexShrink: 0 }}>
          <ErrorOutlineRoundedIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: '#E53935' }} />
        </ListItemIcon>
        <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
          <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.78rem' }, fontWeight: 600, color: 'text.primary', lineHeight: 1.4 }}>
            {page.title}
          </Typography>
          <Typography sx={{ fontSize: { xs: '0.62rem', sm: '0.68rem' }, color: 'text.disabled', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mt: 0.2 }}>
            {page.url}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexShrink: 0, mt: 0.2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {showEditor && !editMode && (
            <Chip label="Generated" size="small" sx={{ bgcolor: 'rgba(67,160,71,0.10)', color: '#43A047', fontWeight: 700, fontSize: '0.65rem', height: 22, border: '1px solid rgba(67,160,71,0.28)', '& .MuiChip-label': { px: 0.9 } }} />
          )}
          {editMode && (
            <Chip label="Editing" size="small" sx={{ bgcolor: `${TEAL_LIGHT}`, color: TEAL, fontWeight: 700, fontSize: '0.65rem', height: 22, border: `1px solid ${TEAL_BORDER}`, '& .MuiChip-label': { px: 0.9 } }} />
          )}
          {generating && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {[0, 1, 2].map(d => (
                <Box key={d} sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: TEAL, animation: `jldDot 1.3s ease-in-out ${d * 0.2}s infinite`, '@keyframes jldDot': { '0%, 80%, 100%': { transform: 'scale(0.5)', opacity: 0.3 }, '40%': { transform: 'scale(1)', opacity: 1 } } }} />
              ))}
            </Box>
          )}
          {!showEditor && !generating && (
            <Button size="small" variant="contained" onClick={handleGenerate}
              startIcon={<AutoAwesome sx={{ fontSize: '13px !important' }} />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: { xs: '0.62rem', sm: '0.68rem' }, px: 1.4, py: 0.5, background: `linear-gradient(135deg, ${TEAL}, #2ec4c9)`, boxShadow: `0 4px 12px rgba(33,146,151,0.28)`, color: '#fff', whiteSpace: 'nowrap', '&:hover': { background: `linear-gradient(135deg, #1a7a7e, ${TEAL})`, transform: 'translateY(-1px)' }, transition: 'all 0.2s' }}>
              Generate with AI
            </Button>
          )}
          {showEditor && !generating && !editMode && (
            <Button size="small" variant="outlined" onClick={handleEnterEdit}
              startIcon={<EditRoundedIcon sx={{ fontSize: '12px !important' }} />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: { xs: '0.62rem', sm: '0.68rem' }, px: 1.3, py: 0.45, borderColor: TEAL_BORDER, color: TEAL, '&:hover': { borderColor: TEAL, bgcolor: TEAL_LIGHT } }}>
              Edit
            </Button>
          )}
          {showEditor && !generating && editMode && (
            <Button size="small" variant="contained" onClick={handleDoneEdit}
              startIcon={<CheckRoundedIcon sx={{ fontSize: '12px !important' }} />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: { xs: '0.62rem', sm: '0.68rem' }, px: 1.4, py: 0.5, background: `linear-gradient(135deg, ${TEAL}, #2ec4c9)`, color: '#fff', boxShadow: `0 4px 12px rgba(33,146,151,0.25)`, '&:hover': { background: `linear-gradient(135deg, #1a7a7e, ${TEAL})` }, transition: 'all 0.2s' }}>
              Done
            </Button>
          )}
          {showEditor && !generating && !editMode && (
            <Button size="small" variant="text" onClick={handleGenerate}
              sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'none', color: 'text.disabled', borderRadius: 2, px: 1, '&:hover': { color: TEAL, bgcolor: TEAL_LIGHT } }}>
              Regenerate
            </Button>
          )}
        </Box>
      </Box>

      <Collapse in={generating} sx={{ width: '100%' }}>
        <Box sx={{ mt: 1.5, borderRadius: 2.5, border: `1.5px dashed ${TEAL_BORDER}`, bgcolor: TEAL_LIGHT, px: { xs: 2, sm: 2.5 }, py: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Box sx={{ position: 'relative', width: 56, height: 56 }}>
            <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', border: `2.5px solid ${TEAL_BORDER}`, borderTopColor: TEAL, animation: 'jldSpin 1.1s linear infinite', '@keyframes jldSpin': { to: { transform: 'rotate(360deg)' } } }} />
            <Box sx={{ position: 'absolute', inset: 10, borderRadius: '50%', border: `1.8px solid rgba(33,146,151,0.18)`, borderBottomColor: TEAL, animation: 'jldSpinRev 0.75s linear infinite', '@keyframes jldSpinRev': { to: { transform: 'rotate(-360deg)' } } }} />
            <Box sx={{ position: 'absolute', inset: 20, borderRadius: '50%', bgcolor: TEAL_LIGHT, border: `1.5px solid ${TEAL_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DataObjectRoundedIcon sx={{ fontSize: 11, color: TEAL }} />
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.88rem' }, fontWeight: 700, color: TEAL, mb: 0.4 }}>Generating JSON-LD</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#000000', lineHeight: 1.65, maxWidth: 320 }}>
              Analysing page content and building structured <code style={{ background: 'rgba(33,146,151,0.12)', borderRadius: 3, padding: '0 4px', fontWeight: 700, color: TEAL }}>JSON-LD</code> schema markup…
            </Typography>
          </Box>
          <Box sx={{ width: '100%', maxWidth: 100 }}>
            <LinearProgress sx={{ height: 5, borderRadius: 3, bgcolor: 'rgba(33,146,151,0.12)', '& .MuiLinearProgress-bar': { borderRadius: 3, background: `linear-gradient(90deg, ${TEAL}, #2ec4c9)` } }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, width: '100%', maxWidth: 290 }}>
            {[
              { label: 'Crawling page content…', delay: '0s' },
              { label: 'Identifying schema types…', delay: '0.8s' },
              { label: 'Building JSON-LD structure…', delay: '1.6s' },
            ].map(({ label, delay }, si) => (
              <Box key={si} sx={{ display: 'flex', alignItems: 'center', gap: 0.8, opacity: 0, animation: `jldFadeUp 0.4s ease forwards`, animationDelay: delay, '@keyframes jldFadeUp': { from: { opacity: 0, transform: 'translateY(5px)' }, to: { opacity: 1, transform: 'translateY(0)' } } }}>
                <Box sx={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, bgcolor: TEAL_LIGHT, border: `1.5px solid ${TEAL_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: TEAL, animation: 'jldPulse 1.4s ease-in-out infinite', '@keyframes jldPulse': { '0%,100%': { transform: 'scale(1)', opacity: 0.7 }, '50%': { transform: 'scale(1.5)', opacity: 1 } } }} />
                </Box>
                <Typography sx={{ fontSize: '0.7rem', color: TEAL, fontWeight: 600 }}>{label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Collapse>

      <Collapse in={!!genError && !generating} sx={{ width: '100%' }}>
        <Box sx={{ mt: 1.5, px: 2, py: 1.5, borderRadius: 2, bgcolor: 'rgba(229,57,53,0.06)', border: '1px solid rgba(229,57,53,0.22)', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <ErrorOutlineRoundedIcon sx={{ fontSize: 15, color: '#E53935', flexShrink: 0, mt: 0.15 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.72rem', color: '#E53935', fontWeight: 700, mb: 0.2 }}>Generation Failed</Typography>
            <Typography sx={{ fontSize: '0.68rem', color: '#E53935', opacity: 0.82 }}>{genError}</Typography>
          </Box>
          <Button size="small" onClick={handleGenerate} sx={{ fontSize: '0.65rem', color: '#E53935', fontWeight: 700, textTransform: 'none', flexShrink: 0, '&:hover': { bgcolor: 'rgba(229,57,53,0.08)' } }}>Retry</Button>
        </Box>
      </Collapse>
      <Collapse in={showEditor && !!jsonLdContent} sx={{ width: '100%' }}>
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: { xs: 1.5, sm: 2 }, py: 1.2,
            background: editMode
              ? `linear-gradient(135deg, #2c5f62 0%, ${TEAL} 100%)`
              : `linear-gradient(135deg, ${TEAL} 0%, #2ec4c9 100%)`,
            borderRadius: editMode ? '10px 10px 0 0' : '10px 10px 0 0',
            transition: 'background 0.3s',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {editMode
                ? <EditRoundedIcon sx={{ fontSize: 16, color: '#fff' }} />
                : <DataObjectRoundedIcon sx={{ fontSize: 16, color: '#fff' }} />
              }
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff' }}>
                {editMode ? 'JSON-LD — Edit Mode' : 'JSON-LD — Generated Successfully'}
              </Typography>
            </Box>
            {!editMode ? (
              <Button size="small" onClick={handleCopy}
                startIcon={copied ? <CheckRoundedIcon sx={{ fontSize: '12px !important' }} /> : <ContentCopyRoundedIcon sx={{ fontSize: '12px !important' }} />}
                sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'none', color: copied ? '#A5F3A0' : '#fff', bgcolor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.28)', borderRadius: 1.5, px: 1.2, py: 0.4, transition: 'all 0.2s', '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' } }}>
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            ) : (
              <Button size="small" onClick={() => setEditMode(false)}
                sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'none', color: 'rgba(255,255,255,0.75)', bgcolor: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 1.5, px: 1.2, py: 0.4, '&:hover': { bgcolor: 'rgba(255,255,255,0.20)', color: '#fff' } }}>
                Cancel
              </Button>
            )}
          </Box>

          {!editMode && (
            <Box
              component="pre"
              sx={{
                m: 0, width: '100%', minHeight: '260px',
                fontFamily: '"IBM Plex Mono", "Fira Mono", monospace',
                fontSize: '12.5px', lineHeight: 1.75,
                p: '14px 18px',
                bgcolor: '#F5FAFA',
                border: '1.5px solid #B2DFDB',
                borderTop: 'none',
                borderRadius: '0 0 10px 10px',
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: '#1a2a2a',
                boxSizing: 'border-box',
              }}
            >
              {jsonLdContent}
            </Box>
          )}

          {editMode && (
            <Box className="aeo-quill-wrap" sx={{ '& .ql-toolbar.ql-snow': { borderRadius: 0 } }}>
              <ReactQuill
                value={quillValue}
                onChange={setQuillValue}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                theme="snow"
              />
            </Box>
          )}

          {!editMode && (
            <Box sx={{ mt: 1.2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1.6, maxWidth: 400 }}>
                Paste this{' '}
                <code style={{ background: 'rgba(33,146,151,0.10)', borderRadius: 3, padding: '1px 4px', color: TEAL, fontWeight: 700 }}>{'<script type="application/ld+json">'}</code>
                {' '}block into the{' '}
                <code style={{ background: 'rgba(33,146,151,0.10)', borderRadius: 3, padding: '1px 4px', color: TEAL, fontWeight: 700 }}>{'<head>'}</code>
                {' '}of this page.
              </Typography>
              <Button size="small" variant="outlined" onClick={handleDownload}
                startIcon={<DownloadRoundedIcon sx={{ fontSize: '13px !important' }} />}
                sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', borderColor: TEAL_BORDER, color: TEAL, borderRadius: 2, flexShrink: 0, '&:hover': { borderColor: TEAL, bgcolor: TEAL_LIGHT } }}>
                Download .json
              </Button>
            </Box>
          )}
          {editMode && (
            <Box sx={{ mt: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
              <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', flex: 1 }}>
                Use the toolbar above to format your JSON-LD. Click <strong>Done</strong> to save changes.
              </Typography>
              <Button size="small" variant="contained" onClick={handleDoneEdit}
                startIcon={<CheckRoundedIcon sx={{ fontSize: '12px !important' }} />}
                sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', background: `linear-gradient(135deg, ${TEAL}, #2ec4c9)`, color: '#fff', borderRadius: 2, px: 2, boxShadow: `0 4px 12px rgba(33,146,151,0.25)`, '&:hover': { background: `linear-gradient(135deg, #1a7a7e, ${TEAL})` } }}>
                Done
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </ListItem>
  )
}

function ComingSoonWrapper({ children, label = 'Coming Soon' }) {
  return (
    <Box sx={{ position: 'relative', borderRadius: 3, overflow: 'hidden' }}>
      <Box sx={{ filter: 'blur(1px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.95 }}>{children}</Box>
      <Box sx={{
        position: 'absolute', inset: 0, borderRadius: 3,
        background: 'linear-gradient(160deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.70) 100%)',
        backdropFilter: 'blur(1px)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 1.5,
        border: `1.5px dashed ${TEAL_BORDER}`,
      }}>
        <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: TEAL_LIGHT, border: `2px solid ${TEAL_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'pulse 2.4s ease-in-out infinite', '@keyframes pulse': { '0%, 100%': { transform: 'scale(1)', opacity: 1 }, '50%': { transform: 'scale(1.08)', opacity: 0.75 } } }}>
          <RocketLaunchRoundedIcon sx={{ fontSize: 22, color: TEAL }} />
        </Box>
        <Box sx={{ textAlign: 'center', px: 2 }}>
          <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' }, fontWeight: 800, color: TEAL, lineHeight: 1.3, mb: 0.4 }}>{label}</Typography>
          <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.78rem' }, color: '#000000', lineHeight: 1.6 }}>We're working on this feature.<br />It will be available very soon.</Typography>
        </Box>
      </Box>
    </Box>
  )
}

function LlmsTxtRow({ exists, status, idx, totalLen, currentDomain }) {
  const [generating, setGenerating] = useState(false)
  const [genError, setGenError]     = useState('')
  const [llmsContent, setLlmsContent] = useState('')
  const [copied, setCopied]         = useState(false)
  const [showEditor, setShowEditor] = useState(false)
  const [editMode, setEditMode]     = useState(false)
  const [quillValue, setQuillValue] = useState('')

  useEffect(() => { ensureQuillStyles() }, [])

  const handleGenerate = async () => {
    setGenerating(true)
    setGenError('')
    setLlmsContent('')
    setShowEditor(false)
    setEditMode(false)
    try {
      const res = await fetch(`${FAST_API_BASE_URL}/api/generate_llms_txt`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'DOMAIN-TOKEN': currentDomain.domain_token },
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json?.detail || json?.message || `Server error: ${res.status}`)
      if (json?.resultCode !== '0' && json?.resultCode !== 0)
        throw new Error(json?.messages?.[0] || 'Generation failed. Please try again.')
      const content = json?.result || ''
      if (!content) throw new Error('No content returned from server.')
      setLlmsContent(content)
      setShowEditor(true)
    } catch (err) {
      setGenError(err.message || 'Failed to generate llms.txt. Please try again.')
    } finally {
      setGenerating(false)
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(llmsContent).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2200)
    })
  }

  const handleDownload = () => {
    const blob = new Blob([llmsContent], { type: 'text/plain' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url
    a.download = 'llms.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleEnterEdit = () => {
    setQuillValue(textToHtml(llmsContent))
    setEditMode(true)
  }

  const handleDoneEdit = () => {
    const plain = htmlToPlain(quillValue)
    setLlmsContent(plain)
    setEditMode(false)
  }

  return (
    <>
      <Box>
        <Box sx={{
          px: { xs: 2, sm: 2.5 }, py: { xs: 1.2, sm: 1.6 },
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
          bgcolor: idx % 2 === 0 ? '#fff' : '#FAFBFC',
          transition: 'background 0.15s',
          '&:hover': { bgcolor: TEAL_LIGHT },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.8, sm: 1.2 }, minWidth: 0, flex: 1 }}>
            <FiberManualRecordRoundedIcon sx={{ fontSize: 7, color: 'text.disabled', flexShrink: 0 }} />
            <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              llms.txt
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {!exists && !generating && !showEditor && (
              <Button size="small" variant="contained" onClick={handleGenerate}
                startIcon={<DescriptionRoundedIcon sx={{ fontSize: '14px !important' }} />}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.72rem' }, px: 1.5, py: 0.55, background: `linear-gradient(135deg, ${TEAL}, #2ec4c9)`, boxShadow: `0 4px 12px rgba(33,146,151,0.28)`, color: '#fff', whiteSpace: 'nowrap', '&:hover': { background: `linear-gradient(135deg, #1a7a7e, ${TEAL})`, transform: 'translateY(-1px)' }, transition: 'all 0.2s' }}>
                Generate with AI
              </Button>
            )}
            {generating && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55 }}>
                {[0, 1, 2].map(d => (
                  <Box key={d} sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: TEAL, animation: `llmsDotBounce 1.3s ease-in-out ${d * 0.2}s infinite`, '@keyframes llmsDotBounce': { '0%, 80%, 100%': { transform: 'scale(0.5)', opacity: 0.3 }, '40%': { transform: 'scale(1)', opacity: 1 } } }} />
                ))}
              </Box>
            )}
            {showEditor && !editMode && (
              <Chip label="Generated" size="small" sx={{ bgcolor: 'rgba(67,160,71,0.10)', color: '#43A047', fontWeight: 700, fontSize: '0.68rem', border: '1px solid rgba(67,160,71,0.28)' }} />
            )}
            {editMode && (
              <Chip label="Editing" size="small" sx={{ bgcolor: TEAL_LIGHT, color: TEAL, fontWeight: 700, fontSize: '0.68rem', border: `1px solid ${TEAL_BORDER}` }} />
            )}
            <CrawlerStatus exists={exists || showEditor} status={showEditor ? 'green' : status} />
          </Box>
        </Box>
        {idx < totalLen - 1 && <Divider sx={{ borderColor: 'rgba(0,0,0,0.05)', mx: { xs: 2, sm: 2.5 } }} />}
      </Box>

      <Collapse in={generating}>
        <Box sx={{ mx: { xs: 1.5, sm: 2.5 }, mb: 2, mt: 0.5, borderRadius: 2.5, border: `1.5px dashed ${TEAL_BORDER}`, bgcolor: TEAL_LIGHT, px: { xs: 2, sm: 3 }, py: { xs: 2.5, sm: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5 }}>
          <Box sx={{ position: 'relative', width: 64, height: 64 }}>
            <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', border: `2.5px solid ${TEAL_BORDER}`, borderTopColor: TEAL, animation: 'llmsSpin 1.1s linear infinite', '@keyframes llmsSpin': { to: { transform: 'rotate(360deg)' } } }} />
            <Box sx={{ position: 'absolute', inset: 11, borderRadius: '50%', border: `1.8px solid rgba(33,146,151,0.18)`, borderBottomColor: TEAL, animation: 'llmsSpinRev 0.75s linear infinite', '@keyframes llmsSpinRev': { to: { transform: 'rotate(-360deg)' } } }} />
            <Box sx={{ position: 'absolute', inset: 22, borderRadius: '50%', bgcolor: TEAL_LIGHT, border: `1.5px solid ${TEAL_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DescriptionRoundedIcon sx={{ fontSize: 13, color: TEAL }} />
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' }, fontWeight: 700, color: TEAL, mb: 0.5 }}>Generating llms.txt</Typography>
            <Typography sx={{ fontSize: '0.73rem', color: '#000000', lineHeight: 1.65, maxWidth: 340 }}>
              Crawling your site structure, extracting key content, and building an AI-optimised <code style={{ background: 'rgba(33,146,151,0.12)', borderRadius: 3, padding: '0 4px', fontWeight: 700, color: TEAL }}>llms.txt</code> file for you…
            </Typography>
          </Box>
          <Box sx={{ width: '100%', maxWidth: 340 }}>
            <LinearProgress sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(33,146,151,0.12)', '& .MuiLinearProgress-bar': { borderRadius: 3, background: `linear-gradient(90deg, ${TEAL}, #2ec4c9)` } }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', maxWidth: 320 }}>
            {[
              { label: 'Crawling site pages…', delay: '0s' },
              { label: 'Extracting key content & links…', delay: '0.9s' },
              { label: 'Building llms.txt structure…', delay: '1.8s' },
            ].map(({ label, delay }, si) => (
              <Box key={si} sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0, animation: `llmsFadeUp 0.45s ease forwards`, animationDelay: delay, '@keyframes llmsFadeUp': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'translateY(0)' } } }}>
                <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: TEAL_LIGHT, border: `1.5px solid ${TEAL_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: TEAL, animation: 'llmsPulse 1.4s ease-in-out infinite', '@keyframes llmsPulse': { '0%,100%': { transform: 'scale(1)', opacity: 0.7 }, '50%': { transform: 'scale(1.5)', opacity: 1 } } }} />
                </Box>
                <Typography sx={{ fontSize: '0.73rem', color: TEAL, fontWeight: 600 }}>{label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Collapse>

      <Collapse in={!!genError && !generating}>
        <Box sx={{ mx: { xs: 1.5, sm: 2.5 }, mb: 2, mt: 0.5, px: 2, py: 1.5, borderRadius: 2, bgcolor: 'rgba(229,57,53,0.06)', border: '1px solid rgba(229,57,53,0.22)', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <ErrorOutlineRoundedIcon sx={{ fontSize: 16, color: '#E53935', flexShrink: 0, mt: 0.15 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#E53935', fontWeight: 700, mb: 0.3 }}>Generation Failed</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#E53935', opacity: 0.82 }}>{genError}</Typography>
          </Box>
          <Button size="small" onClick={handleGenerate} sx={{ fontSize: '0.68rem', color: '#E53935', fontWeight: 700, textTransform: 'none', flexShrink: 0, '&:hover': { bgcolor: 'rgba(229,57,53,0.08)' } }}>Retry</Button>
        </Box>
      </Collapse>

      <Collapse in={showEditor && !!llmsContent}>
        <Box sx={{ mx: { xs: 1.5, sm: 2.5 }, mb: 2.5, mt: 0.5 }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: { xs: 1.5, sm: 2 }, py: 1.3,
            background: editMode
              ? `linear-gradient(135deg, #2c5f62 0%, ${TEAL} 100%)`
              : `linear-gradient(135deg, ${TEAL} 0%, #2ec4c9 100%)`,
            borderRadius: '12px 12px 0 0',
            transition: 'background 0.3s',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {editMode
                ? <EditRoundedIcon sx={{ fontSize: 17, color: '#fff' }} />
                : <DescriptionRoundedIcon sx={{ fontSize: 17, color: '#fff' }} />
              }
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#fff' }}>
                {editMode ? 'llms.txt — Edit Mode' : 'llms.txt — Generated Successfully'}
              </Typography>
            </Box>
            {!editMode ? (
              <Button size="small" onClick={handleCopy}
                startIcon={copied ? <CheckRoundedIcon sx={{ fontSize: '13px !important' }} /> : <ContentCopyRoundedIcon sx={{ fontSize: '13px !important' }} />}
                sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', color: copied ? '#A5F3A0' : '#fff', bgcolor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.28)', borderRadius: 1.5, px: 1.3, py: 0.45, transition: 'all 0.2s', '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' } }}>
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            ) : (
              <Button size="small" onClick={() => setEditMode(false)}
                sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'none', color: 'rgba(255,255,255,0.75)', bgcolor: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 1.5, px: 1.2, py: 0.4, '&:hover': { bgcolor: 'rgba(255,255,255,0.20)', color: '#fff' } }}>
                Cancel
              </Button>
            )}
          </Box>

          {!editMode && (
            <Box
              component="pre"
              sx={{
                m: 0, width: '100%', minHeight: '350px',
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '13px', lineHeight: 1.8,
                p: '16px 20px',
                bgcolor: '#F5FAFA',
                border: '1.5px solid #B2DFDB',
                borderTop: 'none',
                borderRadius: '0 0 12px 12px',
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: '#1a2a2a',
                boxSizing: 'border-box',
              }}
            >
              {llmsContent}
            </Box>
          )}
          {editMode && (
            <Box className="aeo-quill-wrap aeo-quill-llms">
              <ReactQuill
                value={quillValue}
                onChange={setQuillValue}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                theme="snow"
              />
            </Box>
          )}

          {!editMode && (
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', lineHeight: 1.6, maxWidth: 420 }}>
                Place this file at your website root as{' '}
                <code style={{ background: 'rgba(33,146,151,0.10)', borderRadius: 3, padding: '1px 5px', color: TEAL, fontWeight: 700 }}>/llms.txt</code>
                {' '}to improve AI crawler discoverability.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                <Button size="small" variant="outlined" onClick={handleDownload}
                  startIcon={<DescriptionRoundedIcon sx={{ fontSize: '14px !important' }} />}
                  sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'none', borderColor: TEAL_BORDER, color: TEAL, borderRadius: 2, '&:hover': { borderColor: TEAL, bgcolor: TEAL_LIGHT } }}>
                  Download
                </Button>
                <Button size="small" variant="outlined" onClick={handleEnterEdit}
                  startIcon={<EditRoundedIcon sx={{ fontSize: '13px !important' }} />}
                  sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'none', borderColor: TEAL_BORDER, color: TEAL, borderRadius: 2, '&:hover': { borderColor: TEAL, bgcolor: TEAL_LIGHT } }}>
                  Edit
                </Button>
                <Button size="small" variant="text" onClick={handleGenerate}
                  sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'none', color: 'text.disabled', borderRadius: 2, '&:hover': { color: TEAL, bgcolor: TEAL_LIGHT } }}>
                  Regenerate
                </Button>
              </Box>
            </Box>
          )}
          {editMode && (
            <Box sx={{ mt: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
              <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', flex: 1 }}>
                Use the toolbar above to format your content. Click <strong>Done</strong> to save changes.
              </Typography>
              <Button size="small" variant="contained" onClick={handleDoneEdit}
                startIcon={<CheckRoundedIcon sx={{ fontSize: '12px !important' }} />}
                sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', background: `linear-gradient(135deg, ${TEAL}, #2ec4c9)`, color: '#fff', borderRadius: 2, px: 2, boxShadow: `0 4px 12px rgba(33,146,151,0.25)`, '&:hover': { background: `linear-gradient(135deg, #1a7a7e, ${TEAL})` } }}>
                Done
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </>
  )
}

const MODEL_LABEL = {
  CHATGPT: 'ChatGPT',
  GEMINI: 'Gemini',
  CLAUDE: 'Claude',
  PERPLEXITY: 'Perplexity',
  GROK: 'Grok',
}

const MODEL_ICON_URL = {
  CHATGPT: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/04/ChatGPT_logo.svg/240px-ChatGPT_logo.svg.png',
  GEMINI: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8a/Google_Gemini_logo.svg/240px-Google_Gemini_logo.svg.png',
  CLAUDE: 'https://upload.wikimedia.org/wikipedia/commons/0/0f/Claude-ai-icon.png',
  PERPLEXITY: 'https://upload.wikimedia.org/wikipedia/commons/b/b5/Perplexity_AI_Turquoise_on_White.png',
}

function PromptResultCard({ entry, index }) {
  const [expanded, setExpanded] = useState(false)
  const modelKey = (entry.model || '').toUpperCase()
  const modelLabel = MODEL_LABEL[modelKey] || entry.model || 'AI'
  const iconUrl = MODEL_ICON_URL[modelKey]
  const found = entry.target_domain_found === true
  const competitors = entry.competitors || []

  return (
    <Box sx={{
      borderRadius: 2.5,
      border: `1px solid ${found ? 'rgba(67,160,71,0.22)' : 'rgba(0,0,0,0.08)'}`,
      bgcolor: found ? 'rgba(67,160,71,0.03)' : '#fff',
      overflow: 'hidden',
      transition: 'all 0.2s',
      '&:hover': { borderColor: found ? 'rgba(67,160,71,0.4)' : TEAL_BORDER, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' },
    }}>
      <Box
        onClick={() => setExpanded(v => !v)}
        sx={{
          px: { xs: 1.8, sm: 2.2 }, py: { xs: 1.4, sm: 1.6 },
          display: 'flex', alignItems: 'flex-start', gap: 1.5, cursor: 'pointer',
        }}
      >
        <Box sx={{
          width: 26, height: 26, borderRadius: '8px', flexShrink: 0,
          bgcolor: TEAL_LIGHT, border: `1.5px solid ${TEAL_BORDER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.2,
        }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: TEAL, lineHeight: 1 }}>
            {index + 1}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: { xs: '0.78rem', sm: '0.84rem' }, fontWeight: 600,
            color: 'text.primary', lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {entry.prompt}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.7, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.9, py: 0.25, borderRadius: 1, bgcolor: '#F3F4F6', border: '1px solid rgba(0,0,0,0.08)' }}>
              {iconUrl && (
                <Box component="img" src={iconUrl} alt={modelLabel}
                  sx={{ width: 13, height: 13, objectFit: 'contain' }}
                  onError={e => { e.target.style.display = 'none' }}
                />
              )}
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary' }}>{modelLabel}</Typography>
            </Box>
            {found ? (
              <Chip
                size="small"
                icon={<CheckCircleRoundedIcon sx={{ fontSize: '13px !important', color: '#43A047 !important' }} />}
                label="Your domain cited"
                sx={{ height: 22, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(67,160,71,0.10)', color: '#43A047', border: '1px solid rgba(67,160,71,0.28)', '& .MuiChip-label': { px: 0.8 } }}
              />
            ) : (
              <Chip
                size="small"
                icon={<CancelRoundedIcon sx={{ fontSize: '13px !important', color: '#E53935 !important' }} />}
                label="Not cited"
                sx={{ height: 22, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(229,57,53,0.08)', color: '#E53935', border: '1px solid rgba(229,57,53,0.22)', '& .MuiChip-label': { px: 0.8 } }}
              />
            )}
            {competitors.length > 0 && (
              <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', fontWeight: 500 }}>
                {competitors.length} competitor{competitors.length !== 1 ? 's' : ''} cited
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ flexShrink: 0, mt: 0.2 }}>
          {expanded
            ? <ExpandLessIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
            : <ExpandMoreIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
          }
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />

        {competitors.length > 0 && (
          <Box sx={{ px: { xs: 1.8, sm: 2.2 }, pt: 1.4, pb: 0.6 }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.8 }}>
              Competitors cited in this response
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
              {competitors.map((c, i) => (
                <Chip key={i} label={c} size="small" sx={{
                  height: 22, fontSize: '0.68rem', fontWeight: 500,
                  bgcolor: 'rgba(53, 223, 229, 0.06)', color: '#219297',
                  border: '1px solid rgba(53, 229, 200, 0.18)',
                  '& .MuiChip-label': { px: 1 },
                }} />
              ))}
            </Box>
          </Box>
        )}

        {entry.response && (
          <Box sx={{ px: { xs: 1.8, sm: 2.2 }, pt: competitors.length > 0 ? 1 : 1.4, pb: 1.8 }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.8 }}>
              AI response
            </Typography>
            <Box sx={{
              p: { xs: 1.4, sm: 1.8 }, borderRadius: 2,
              bgcolor: '#F8FAFA', border: `1.5px solid ${TEAL_BORDER}`,
              maxHeight: 260, overflowY: 'auto',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(33,146,151,0.25)', borderRadius: 10 },
            }}>
              <Typography sx={{
                fontSize: { xs: '0.72rem', sm: '0.76rem' }, color: '#1a2a2a',
                lineHeight: 1.75, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                fontFamily: '"IBM Plex Mono", monospace',
              }}>
                {entry.response}
              </Typography>
            </Box>
          </Box>
        )}
      </Collapse>
    </Box>
  )
}

function AiPresenceCard({ aiPresenceReport }) {
  if (!aiPresenceReport || aiPresenceReport.length === 0) return null

  const totalPrompts = aiPresenceReport.length
  const citedCount = aiPresenceReport.filter(e => e.target_domain_found === true).length
  const notCitedCount = totalPrompts - citedCount
  const citationRate = totalPrompts > 0 ? Math.round((citedCount / totalPrompts) * 100) : 0

  const allCompetitors = new Set()
  aiPresenceReport.forEach(e => (e.competitors || []).forEach(c => allCompetitors.add(c)))
  const competitorList = Array.from(allCompetitors)

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid rgba(0,0,0,0.05)', overflow: 'hidden' }}>

      <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2.5 }, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
              <AutoAwesomeRoundedIcon sx={{ fontSize: 18, color: TEAL }} />
              <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1rem', md: '1.0625rem' }, fontWeight: 700, color: 'text.primary' }}>
                AI Presence
              </Typography>
            </Box>
            <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.78rem' }, color: '#000000', lineHeight: 1.6 }}>
              How often your domain is cited when AI models answer industry-relevant queries.
            </Typography>
          </Box>
          {/* <MiniScoreRing score={citationRate} /> */}
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', bgcolor: '#FAFBFC' }}>
        {[
          { label: 'Prompts tested', value: totalPrompts, color: 'text.primary' },
          { label: 'Times cited', value: citedCount, color: citedCount > 0 ? '#43A047' : 'text.disabled' },
          { label: 'Not cited', value: notCitedCount, color: notCitedCount > 0 ? '#E53935' : 'text.disabled' },
        ].map(({ label, value, color }, idx) => (
          <Box key={label} sx={{
            px: { xs: 1.5, sm: 2 }, py: { xs: 1.4, sm: 1.8 },
            borderRight: idx < 2 ? '1px solid rgba(0,0,0,0.06)' : 'none',
            '&:hover': { bgcolor: TEAL_LIGHT }, transition: 'background 0.15s',
          }}>
            <Typography sx={{ fontSize: { xs: '0.62rem', sm: '0.68rem' }, color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
              {label}
            </Typography>
            <Typography sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' }, fontWeight: 800, color, lineHeight: 1 }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
      {competitorList.length > 0 && (
        <>
          <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
          <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1.2, sm: 1.6 }, bgcolor: '#fff' }}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.8 }}>
              Competitors appearing across all responses
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
              {competitorList.slice(0, 8).map((c, i) => (
                <Chip key={i} label={c} size="small" sx={{
                  bgcolor: 'rgba(53, 217, 229, 0.06)', color: '#219297',
                  border: '1px solid rgba(53, 229, 176, 0.18)', fontSize: '0.7rem',
                  fontWeight: 500, height: 24, '& .MuiChip-label': { px: 1 },
                }} />
              ))}
              {competitorList.length > 8 && (
                <Chip label={`+${competitorList.length - 8} more`} size="small" sx={{
                  bgcolor: '#F3F4F6', color: 'text.secondary', border: '1px solid rgba(0,0,0,0.1)',
                  fontSize: '0.7rem', height: 24, '& .MuiChip-label': { px: 1 },
                }} />
              )}
            </Box>
          </Box>
        </>
      )}

      <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />

      {/* Per-prompt results */}
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.2 }}>
          Prompt-level results ({totalPrompts})
        </Typography>
        <Stack spacing={1.2}>
          {aiPresenceReport.map((entry, idx) => (
            <PromptResultCard key={idx} entry={entry} index={idx} />
          ))}
        </Stack>
      </Box>
    </Card>
  )
}

function CompetitorLandscapeCard({ competitorData }) {
  if (!competitorData) return null
  const overallScore = Math.round(competitorData.score || 0)
  const result = competitorData.result || {}
  const providers = ['openai', 'gemini']
  const allCompetitors = new Set()
  providers.forEach(pk => {
    const pd = result[pk]
    if (!pd) return
    Object.entries(pd).forEach(([k, v]) => { if (k !== 'score' && v?.competitors) v.competitors.forEach(c => allCompetitors.add(c)) })
  })
  const competitorList = Array.from(allCompetitors)
  const totalQueries = (() => { let c = 0; providers.forEach(pk => { const pd = result[pk]; if (!pd) return; Object.entries(pd).forEach(([k]) => { if (k !== 'score') c++ }) }); return c })()
  const includedCount = (() => { let c = 0; providers.forEach(pk => { const pd = result[pk]; if (!pd) return; Object.entries(pd).forEach(([k, v]) => { if (k !== 'score' && v?.included) c++ }) }); return c })()
  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid rgba(0,0,0,0.05)', overflow: 'hidden' }}>
      <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2.5 }, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
              <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1rem', md: '1.0625rem' }, fontWeight: 700, color: 'text.primary' }}>
                Competitor Landscape
              </Typography>
            </Box>
            <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.78rem' }, color: '#000000', lineHeight: 1.6 }}>
              Which companies are mentioned in industry/product related queries?
            </Typography>
          </Box>
          <Typography sx={{ fontSize: { xs: '1.4rem', sm: '1.6rem' }, fontWeight: 900, color: overallScore > 0 ? '#43A047' : 'text.disabled', flexShrink: 0 }}>
            {overallScore}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />

      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1.5, sm: 2 }, bgcolor: '#FAFBFC' }}>
        <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, color: '#000000', lineHeight: 1.7 }}>
          Your company or product was included{' '}
          <Typography component="span" sx={{ fontWeight: 700, color: includedCount > 0 ? '#43A047' : 'text.primary' }}>
            {includedCount}
          </Typography>{' '}
          out of{' '}
          <Typography component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {totalQueries}
          </Typography>{' '}
          times in industry/product related queries.
        </Typography>

        {competitorList.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.8 }}>
              Competitors Mentioned:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
              {competitorList.slice(0, 3).map((c, i) => (
                <Chip key={i} label={c} size="small" sx={{ bgcolor: '#fff', border: '1px solid rgba(0,0,0,0.12)', fontSize: '0.72rem', color: '#000000', fontWeight: 500 }} />
              ))}
              {competitorList.length > 3 && (
                <Chip label={`${competitorList.length - 3} others...`} size="small" sx={{ bgcolor: '#fff', border: '1px solid rgba(0,0,0,0.12)', fontSize: '0.72rem', color: '#000000', fontWeight: 500 }} />
              )}
            </Box>
          </Box>
        )}
      </Box>
      <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />

      {providers.map((providerKey, pIdx) => {
        const providerData = result[providerKey]
        const config = PROVIDER_CONFIG[providerKey]
        if (!config) return null

        const isNull = providerData === null || providerData === undefined
        const providerScore = isNull ? null : Math.round(providerData.score || 0)
        const modelEntries = isNull
          ? []
          : Object.entries(providerData).filter(([k]) => k !== 'score')

        return (
          <Box key={providerKey}>
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: { xs: 2, sm: 2.5 }, py: { xs: 1.2, sm: 1.5 },
              bgcolor: pIdx % 2 === 0 ? '#fff' : '#FAFBFC',
              borderBottom: '1px solid rgba(0,0,0,0.05)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: '10px', bgcolor: '#F0F0F0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {config.icon}
                </Box>
                <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, fontWeight: 700, color: 'text.primary' }}>
                  {config.label}
                </Typography>
              </Box>
              {isNull ? (
                <LockRoundedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              ) : (
                <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, fontWeight: 800, color: 'text.primary' }}>
                  {providerScore}
                </Typography>
              )}
            </Box>

            {modelEntries.map(([modelKey, modelData]) => {
              const modelLabel = config.modelLabels?.[modelKey] || modelKey
              const competitors = modelData?.competitors || []
              return (
                <Box key={modelKey} sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1, sm: 1.2 }, bgcolor: pIdx % 2 === 0 ? '#fff' : '#FAFBFC', borderBottom: '1px solid rgba(0,0,0,0.04)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, '&:last-of-type': { borderBottom: '1px solid rgba(0,0,0,0.06)' } }}>
                  <Box sx={{ pl: { xs: 5, sm: 6.5 }, flexShrink: 0 }}>
                    <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 600, color: '#000000' }}>{modelLabel}</Typography>
                    {config.hasSearch && <Typography component="span" sx={{ fontSize: '0.68rem', color: 'text.disabled', ml: 0.5 }}>+ Search</Typography>}
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, justifyContent: 'flex-end', flex: 1 }}>
                    {competitors.map((c, i) => <Chip key={i} label={c} size="small" sx={{ bgcolor: '#F5F5F5', border: '1px solid rgba(0,0,0,0.09)', fontSize: '0.68rem', color: '#000000', fontWeight: 500, height: 22, '& .MuiChip-label': { px: 1 } }} />)}
                  </Box>
                </Box>
              )
            })}
          </Box>
        )
      })}
    </Card>
  )
}

function WebPresenceCard({ webPresenceData }) {
  if (!webPresenceData) return null
  const totalScore = Math.round(webPresenceData.totalScore || 0)
  const wiki = webPresenceData.wikipedia || {}
  const hasWiki = wiki.hasWikipediaPage === true
  const scoreColor = totalScore >= 70 ? '#43A047' : totalScore >= 40 ? '#FB8C00' : '#E53935'
  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid rgba(0,0,0,0.05)', overflow: 'hidden' }}>
      <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2.5 }, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box>
            <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1rem', md: '1.0625rem' }, fontWeight: 700, color: 'text.primary', mb: 0.4 }}>Web Presence</Typography>
            <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.78rem' }, color: '#000000', lineHeight: 1.6 }}>How established your brand is across major web platforms and references.</Typography>
          </Box>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography sx={{ fontSize: { xs: '1.6rem', sm: '1.9rem' }, fontWeight: 900, color: scoreColor, lineHeight: 1 }}>{totalScore}</Typography>
            <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', fontWeight: 600 }}>/100</Typography>
          </Box>
        </Box>
      </Box>
      <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '10px', flexShrink: 0, bgcolor: '#f8f8f8', border: '1px solid rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            <Box component="img" src="https://upload.wikimedia.org/wikipedia/commons/0/07/Wikipedia_logo_%28svg%29.svg" alt="Wikipedia" sx={{ width: '75%', height: '75%', objectFit: 'contain' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, fontWeight: 700, color: 'text.primary' }}>Wikipedia</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#000000' }}>Brand page presence &amp; authority</Typography>
          </Box>
          {hasWiki
            ? <Chip label="Page Found" size="small" icon={<CheckCircleRoundedIcon sx={{ fontSize: '14px !important', color: '#43A047 !important' }} />} sx={{ bgcolor: 'rgba(67,160,71,0.08)', color: '#43A047', fontWeight: 700, fontSize: '0.68rem', border: '1px solid rgba(67,160,71,0.22)' }} />
            : <Chip label="No Page" size="small" icon={<CancelRoundedIcon sx={{ fontSize: '14px !important', color: '#E53935 !important' }} />} sx={{ bgcolor: 'rgba(229,57,53,0.08)', color: '#E53935', fontWeight: 700, fontSize: '0.68rem', border: '1px solid rgba(229,57,53,0.22)' }} />
          }
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: { xs: 1, sm: 1.5 }, mt: 1 }}>
          {[
            { label: 'Sections', value: wiki.sectionsCount ?? 0, icon: '§', active: hasWiki },
            { label: 'Backlinks', value: wiki.backlinksCount ?? 0, icon: '↗', active: hasWiki },
            { label: 'Raw Score', value: (wiki.rawScore ?? 0).toFixed(1), icon: '◎', active: hasWiki },
            { label: 'Weighted Score', value: (wiki.weightedScore ?? 0).toFixed(1), icon: '★', active: hasWiki },
          ].map(({ label, value, icon, active }) => (
            <Box key={label} sx={{ p: { xs: 1.2, sm: 1.5 }, borderRadius: 2, bgcolor: active ? '#FAFBFC' : 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 1, transition: 'all 0.2s', '&:hover': active ? { borderColor: TEAL_BORDER, bgcolor: TEAL_LIGHT } : {} }}>
              <Typography sx={{ fontSize: '1rem', lineHeight: 1, color: active ? TEAL : 'text.disabled', flexShrink: 0 }}>{icon}</Typography>
              <Box>
                <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', lineHeight: 1, mb: 0.3 }}>{label}</Typography>
                <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' }, fontWeight: 800, color: active ? 'text.primary' : 'text.disabled', lineHeight: 1 }}>{active ? value : '—'}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
        {hasWiki && wiki.wikipediaUrl && (
          <Box sx={{ mt: 1.5, px: 1.5, py: 1, borderRadius: 1.5, bgcolor: TEAL_LIGHT, border: `1px solid ${TEAL_BORDER}`, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LanguageRoundedIcon sx={{ fontSize: 14, color: TEAL, flexShrink: 0 }} />
            <Typography component="a" href={wiki.wikipediaUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.72rem', color: TEAL, fontWeight: 600, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, '&:hover': { textDecoration: 'underline' } }}>{wiki.wikipediaUrl}</Typography>
          </Box>
        )}
        {!hasWiki && (
          <Box sx={{ mt: 1.5, px: 1.5, py: 1.2, borderRadius: 1.5, bgcolor: 'rgba(251,140,0,0.06)', border: '1px solid rgba(251,140,0,0.2)', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <WarningAmberRoundedIcon sx={{ fontSize: 15, color: '#FB8C00', flexShrink: 0, mt: 0.1 }} />
            <Typography sx={{ fontSize: '0.72rem', color: '#FB8C00', fontWeight: 500, lineHeight: 1.5 }}>No Wikipedia page found. A Wikipedia presence significantly boosts AI citation visibility.</Typography>
          </Box>
        )}
      </Box>
    </Card>
  )
}

function ImpactStars({ impact }) {
  const max = 5
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
      {Array.from({ length: max }).map((_, i) => (
        i < impact
          ? <StarRoundedIcon key={i} sx={{ fontSize: 14, color: '#F59E0B' }} />
          : <StarBorderRoundedIcon key={i} sx={{ fontSize: 14, color: '#D1D5DB' }} />
      ))}
    </Box>
  )
}

function ActionPlanCard({ plan, index }) {
  return (
    <Box sx={{
      minWidth: { xs: 260, sm: 300 },
      maxWidth: { xs: 280, sm: 320 },
      flexShrink: 0,
      borderRadius: 2.5,
      border: '1px solid rgba(0,0,0,0.08)',
      bgcolor: '#fff',
      p: { xs: 1.8, sm: 2.2 },
      display: 'flex',
      flexDirection: 'column',
      gap: 1.2,
      transition: 'all 0.2s',
      '&:hover': {
        borderColor: TEAL_BORDER,
        boxShadow: '0 4px 16px rgba(33,146,151,0.12)',
        transform: 'translateY(-2px)',
      },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
        <Box sx={{
          width: 26, height: 26, borderRadius: '8px', flexShrink: 0,
          bgcolor: TEAL_LIGHT, border: `1.5px solid ${TEAL_BORDER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: TEAL, lineHeight: 1 }}>
            {index + 1}
          </Typography>
        </Box>
        <Typography sx={{
          fontSize: { xs: '0.8rem', sm: '1.0rem' },
          fontWeight: 700,
          color: 'text.primary',
          lineHeight: 1.35,
          flex: 1,
        }}>
          {plan.title}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Impact:
        </Typography>
        <ImpactStars impact={plan.impact} />
      </Box>

      <Typography sx={{
        fontSize: { xs: '0.72rem', sm: '0.9rem' },
        color: '#252424',
        lineHeight: 1.65,
        flex: 1,
      }}>
        {plan.description}
      </Typography>
    </Box>
  )
}

function ActionPlanSection({ tasks, section }) {
  if (!tasks) return null
  const task = tasks.find(t => t.section === section)
  if (!task || !task.action_plans || task.action_plans.length === 0) return null

  return (
    <Box sx={{
      borderRadius: 3,
      border: '1px solid rgba(0,0,0,0.07)',
      bgcolor: '#FAFBFC',
      overflow: 'hidden',
    }}>
      <Box sx={{
        px: { xs: 2, sm: 2.5 }, py: { xs: 1.4, sm: 1.8 },
        display: 'flex', alignItems: 'center', gap: 1,
        borderBottom: '1px solid rgba(0,0,0,0.07)',
        bgcolor: '#fff',
      }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: '8px',
          bgcolor: TEAL_LIGHT, border: `1.5px solid ${TEAL_BORDER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <PsychologyRoundedIcon sx={{ fontSize: 16, color: TEAL }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.88rem' }, fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
            Action Plan
          </Typography>
          <Typography sx={{ fontSize: '0.70rem', color: 'text.disabled', fontWeight: 500 }}>
            {task.action_plans.length} recommended action{task.action_plans.length !== 1 ? 's' : ''} to improve AEO score
          </Typography>
        </Box>
      </Box>

      <Box sx={{
        display: 'flex',
        gap: { xs: 1.5, sm: 2 },
        overflowX: 'auto',
        p: { xs: 1.5, sm: 2 },
        pb: { xs: 2, sm: 2.5 },
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(33,146,151,0.25)', borderRadius: 10 },
        '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'rgba(33,146,151,0.45)' },
      }}>
        {task.action_plans
          .slice()
          .sort((a, b) => b.impact - a.impact)
          .map((plan, idx) => (
            <ActionPlanCard key={idx} plan={plan} index={idx} />
          ))}
      </Box>
    </Box>
  )
}

function ProcessingScreen({ progress, elapsedTime }) {
  const activeStep = Math.min(
    Math.floor(progress * ANALYSIS_STEPS.length),
    ANALYSIS_STEPS.length - 1
  )

  const displayProgress = Math.max(progress * 100, 5)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', px: { xs: 2, sm: 4 } }}>
      <Box sx={{ position: 'relative', width: 160, height: 160, mb: 4 }}>
        <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', border: `3px solid ${TEAL_BORDER}`, animation: 'spin 3s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } }, borderTopColor: TEAL }} />
        <Box sx={{ position: 'absolute', inset: 18, borderRadius: '50%', border: `2px solid rgba(33,146,151,0.15)`, animation: 'spinReverse 2s linear infinite', '@keyframes spinReverse': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(-360deg)' } }, borderBottomColor: TEAL }} />
        <Box sx={{ position: 'absolute', inset: 36, borderRadius: '50%', bgcolor: TEAL_LIGHT, border: `2px solid ${TEAL_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'pulse 2s ease-in-out infinite', '@keyframes pulse': { '0%, 100%': { transform: 'scale(1)', opacity: 1 }, '50%': { transform: 'scale(0.92)', opacity: 0.7 } } }}>
          <AutoAwesomeRoundedIcon sx={{ fontSize: 30, color: TEAL }} />
        </Box>
      </Box>

      <Typography sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 800, color: 'text.primary', mb: 1, textAlign: 'center' }}>
        Analysing Your Website
      </Typography>
      <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, color: '#000000', mb: 4, textAlign: 'center' }}>
        Running a full AEO analysis
      </Typography>

      <Box sx={{ width: '100%', maxWidth: 480, mb: 1 }}>
        <LinearProgress variant="determinate" value={displayProgress} sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(0,0,0,0.06)', '& .MuiLinearProgress-bar': { borderRadius: 4, background: `linear-gradient(90deg, ${TEAL}, #2ec4c9)` } }} />
      </Box>

      <Box sx={{ width: '100%', maxWidth: 440 }}>
        {ANALYSIS_STEPS.map((step, idx) => {
          const isActive = idx === activeStep
          const isDone = idx < activeStep
          return (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.2, px: 2, mb: 0.5, borderRadius: 2, bgcolor: isActive ? TEAL_LIGHT : 'transparent', border: `1px solid ${isActive ? TEAL_BORDER : 'transparent'}`, transition: 'all 0.4s ease', opacity: isDone ? 0.45 : 1 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, bgcolor: isDone ? 'rgba(67,160,71,0.12)' : isActive ? TEAL_LIGHT : 'rgba(0,0,0,0.04)', border: `2px solid ${isDone ? '#43A047' : isActive ? TEAL : 'rgba(0,0,0,0.08)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.4s ease' }}>
                {isDone ? <CheckCircleRoundedIcon sx={{ fontSize: 16, color: '#43A047' }} /> : React.cloneElement(step.icon, { sx: { fontSize: 16, color: isActive ? TEAL : 'text.disabled' } })}
              </Box>
              <Typography sx={{ fontSize: { xs: '0.78rem', sm: '0.875rem' }, fontWeight: isActive ? 700 : 500, color: isDone ? 'text.disabled' : isActive ? TEAL : '#000000', transition: 'all 0.4s ease' }}>{step.label}</Typography>
              {isActive && (
                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.4 }}>
                  {[0, 1, 2].map(d => <Box key={d} sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: TEAL, animation: `dotBounce 1.2s ease-in-out ${d * 0.2}s infinite`, '@keyframes dotBounce': { '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 }, '40%': { transform: 'scale(1)', opacity: 1 } } }} />)}
                </Box>
              )}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

function LandingScreen({ onStart, isStarting, error, useMockData, onToggleMock }) {
  const features = [
    { icon: <PsychologyRoundedIcon />, title: 'Content Answerability', desc: 'How well your content answers user questions in AI chat engines' },
    { icon: <TrackChangesRoundedIcon />, title: 'Crawler Accessibility', desc: 'Whether major AI bots (GPT, Claude, Gemini…) can index your site' },
    { icon: <BarChartRoundedIcon />, title: 'Structured Data', desc: 'Schema markup, semantic HTML, and metadata quality check' },
    { icon: <SpeedRoundedIcon />, title: 'Performance Analysis', desc: 'Page speed and overall readiness' },
  ]
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', px: { xs: 2, sm: 4 }, py: { xs: 4, sm: 6 } }}>
      <Chip label="Answer Engine Optimization" size="small" sx={{ mb: 3, bgcolor: TEAL_LIGHT, color: TEAL, fontWeight: 700, fontSize: '0.72rem', border: `1px solid ${TEAL_BORDER}`, letterSpacing: '0.04em' }} />
      <Typography sx={{ fontSize: { xs: '1.6rem', sm: '2rem', md: '2.4rem' }, fontWeight: 900, color: 'text.primary', textAlign: 'center', lineHeight: 1.2, mb: 1.5, letterSpacing: '-0.02em' }}>Check Your Website's AEO Score</Typography>
      <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, color: '#000000', textAlign: 'center', lineHeight: 1.7, mb: 4, maxWidth: 560 }}>Answer Engine Optimization improves your website's visibility to AI's like ChatGPT and Gemini</Typography>
      <Button variant="contained" size="large" onClick={onStart} disabled={isStarting}
        startIcon={isStarting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SearchRoundedIcon />}
        sx={{ mb: error ? 1.5 : 4, px: 4, py: 1.6, borderRadius: 3, fontSize: { xs: '0.9rem', sm: '1rem' }, fontWeight: 700, background: `linear-gradient(135deg, ${TEAL}, #2ec4c9)`, boxShadow: `0 8px 24px rgba(33,146,151,0.35)`, textTransform: 'none', letterSpacing: 0, color: '#fff', transition: 'all 0.2s', '&:hover': { background: `linear-gradient(135deg, #1a7a7e, ${TEAL})`, boxShadow: `0 12px 32px rgba(33,146,151,0.45)`, transform: 'translateY(-2px)' }, '&:disabled': { background: 'rgba(0,0,0,0.12)', boxShadow: 'none', transform: 'none' } }}>
        {isStarting ? 'Analysing…' : 'Check my AEO Score'}
      </Button>
            {/* <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.2, px: 2, py: 1, borderRadius: 2, bgcolor: useMockData ? 'rgba(33,146,151,0.08)' : 'rgba(0,0,0,0.03)', border: `1px dashed ${useMockData ? TEAL_BORDER : 'rgba(0,0,0,0.12)'}`, cursor: 'pointer', transition: 'all 0.2s', userSelect: 'none' }} onClick={onToggleMock}>
        <Box sx={{ width: 32, height: 18, borderRadius: 9, bgcolor: useMockData ? TEAL : 'rgba(0,0,0,0.18)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
          <Box sx={{ position: 'absolute', top: 2, left: useMockData ? 16 : 2, width: 14, height: 14, borderRadius: '50%', bgcolor: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
        </Box>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: useMockData ? TEAL : 'text.disabled' }}>
          Use mock data {useMockData ? '(ON — no API call)' : '(OFF — hits real API)'}
        </Typography>
        <LightbulbRoundedIcon sx={{ fontSize: 15, color: useMockData ? TEAL : 'text.disabled', ml: 'auto' }} />
      </Box> */}
      {error && (
        <Box sx={{ mb: 3, px: 2.5, py: 1.5, borderRadius: 2, bgcolor: 'rgba(229,57,53,0.08)', border: '1px solid rgba(229,57,53,0.22)', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: '#E53935', flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.78rem', color: '#E53935', fontWeight: 500 }}>{error}</Typography>
        </Box>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 1.5, sm: 2 }, width: '100%', maxWidth: 800 }}>
        {features.map(({ icon, title, desc }) => (
          <Box key={title} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, bgcolor: '#FAFBFC', border: '1px solid rgba(0,0,0,0.06)', transition: 'all 0.2s', '&:hover': { borderColor: TEAL_BORDER, bgcolor: TEAL_LIGHT, transform: 'translateY(-3px)', boxShadow: '0 6px 20px rgba(0,0,0,0.07)' } }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: TEAL_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
              {React.cloneElement(icon, { sx: { fontSize: 20, color: TEAL } })}
            </Box>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.primary', mb: 0.5, lineHeight: 1.4 }}>{title}</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#000000', lineHeight: 1.6 }}>{desc}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

function normalizeReport(report) {
  if (!report) return {}
  const fr = report.finalReport || {}
  const companyInfo = {
    name: fr.companyInfo?.name,
    industry: fr.companyInfo?.industry,
    description: fr.companyInfo?.description,
    location: fr.companyInfo?.location,
    keyProducts: fr.companyInfo?.keyProducts,
    industry_product_clarity_score: fr.companyInfo?.industryProductClarityScore,
    answerability: {
      totalPhrases: fr.answerability?.totalPhrases,
      isGoodLengthPhrase: fr.answerability?.isGoodLengthPhrase,
      isConversationalPhrase: fr.answerability?.isConversationalPhrase,
      hasStatisticsPhrase: fr.answerability?.hasStatisticsPhrase,
      hasCitationPhrase: fr.answerability?.hasCitationPhrase,
      hasCitationsSection: fr.answerability?.hasCitationsSection,
      strengths: fr.answerability?.strengths,
      weaknesses: fr.answerability?.weaknesses,
      answerability_score: fr.answerability?.answerabilityScore,
    },
  }
  const ai_crawler_accessibility = {
    accessibility_score: fr.aiCrawlerAccessibility?.accessibility_score,
    technical_files: {
      sitemap_xml: fr.aiCrawlerAccessibility?.technicalFiles?.sitemapXml,
      robots_txt: fr.aiCrawlerAccessibility?.technicalFiles?.robotsTxt,
      llms_txt: fr.aiCrawlerAccessibility?.technicalFiles?.llmsTxt,
    },
    ai_bot_permissions: {
      allowed_count: fr.aiCrawlerAccessibility?.aiBotPermissions?.allowedCount,
      total_bots_checked: fr.aiCrawlerAccessibility?.aiBotPermissions?.totalBotsChecked,
      details: fr.aiCrawlerAccessibility?.aiBotPermissions?.details,
    },
    language_detection: fr.aiCrawlerAccessibility?.language_detection,
    rendering_analysis: fr.aiCrawlerAccessibility?.rendering_analysis,
    performance_analysis: fr.aiCrawlerAccessibility?.performance_analysis,
  }
  const structure_data_details = {
    schema_details: {
      schema_markup_details: {
        schema_markup_present: fr.structureDataDetails?.schemaMarkupDetails?.schemaMarkupPresent,
        pages_without_schema_markups: fr.structureDataDetails?.schemaMarkupDetails?.pagesWithoutSchemaMarkups,
        schema_markup_missing_pages: fr.structureDataDetails?.schemaMarkupDetails?.schemaMarkupMissingPages,
      },
    },
    semantic_details: {
      semantic_elements: {
        unique_types_found: fr.structureDataDetails?.semanticDetails?.uniqueTypesFound,
        all_tags_count: fr.structureDataDetails?.semanticDetails?.allTagsCount,
        semantic_tags_count: fr.structureDataDetails?.semanticDetails?.semanticTagsCount,
        non_semantic_tags_count: fr.structureDataDetails?.semanticDetails?.nonSemanticTagsCount,
      },
    },
  }
  // console.log(report)
  // console.log(fr.aiPresenceReport)
  const ai_presence_report = fr.aiPresenceReport
    ? Object.values(fr.aiPresenceReport)
    : []
// console.log(ai_presence_report)
  return {
    companyInfo,
    ai_crawler_accessibility,
    structure_data_details,
    final_aeo_score: report.finalAeoScore,
    ai_presence_report,
    competitor_landscape: null,
    web_presence: fr.webPresence ? { totalScore: fr.webPresence.totalScore, wikipedia: fr.webPresence.wikipedia } : null,
  }
  
}

function ScoreBar({ score }) {
  const color = score >= 70 ? '#43A047' : score >= 40 ? TEAL : score >= 20 ? '#FB8C00' : '#E53935'
  const pct = Math.min(Math.round(score), 100)
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color, minWidth: 42, flexShrink: 0 }}>
        {score.toFixed(1)}%
      </Typography>
      <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.07)', overflow: 'hidden' }}>
        <Box sx={{
          height: '100%', borderRadius: 3,
          width: `${pct}%`,
          background: `linear-gradient(90deg, ${color}99, ${color})`,
          transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)',
        }} />
      </Box>
    </Box>
  )
}

function HistoryScreen({ history, onSelect, onNewScan, isLoadingReport, selectedJobId }) {
  return (
    <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: { xs: '1.15rem', sm: '1.35rem', md: '1.5rem' }, fontWeight: 700, mb: 0.4 }}>
            AEO Reports
          </Typography>
          <Typography sx={{ fontSize: { xs: '0.78rem', sm: '0.875rem' }, color: '#000000' }}>
            {history.length} previous scan{history.length !== 1 ? 's' : ''} found
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={onNewScan}
          startIcon={<SearchRoundedIcon />}
          sx={{
            borderRadius: 2, textTransform: 'none', fontWeight: 700,
            fontSize: '0.82rem', px: 2.5, py: 1,
            background: `linear-gradient(135deg, ${TEAL}, #2ec4c9)`,
            boxShadow: `0 4px 14px rgba(33,146,151,0.3)`,
            color: '#fff',
            '&:hover': { background: `linear-gradient(135deg, #1a7a7e, ${TEAL})`, transform: 'translateY(-1px)' },
            transition: 'all 0.2s',
          }}
        >
          New Scan
        </Button>
      </Box>

      <Stack spacing={1.5}>
        {history.map((item) => {
          const isLoading = isLoadingReport && selectedJobId === item.jobId
          const date = item.createdAt ? new Date(item.createdAt) : null
          const dateStr = date
            ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
              ' at ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
            : '—'

          return (
            <Card
              key={item.jobId}
              onClick={() => !isLoadingReport && onSelect(item.jobId)}
              sx={{
                borderRadius: 2.5,
                border: '1px solid rgba(0,0,0,0.07)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                cursor: isLoadingReport ? 'default' : 'pointer',
                transition: 'all 0.2s',
                opacity: isLoadingReport && selectedJobId !== item.jobId ? 0.55 : 1,
                '&:hover': !isLoadingReport ? {
                  borderColor: TEAL_BORDER,
                  boxShadow: '0 4px 18px rgba(33,146,151,0.12)',
                  transform: 'translateY(-2px)',
                } : {},
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 1.8, sm: 2 }, pb: { xs: 1.5, sm: 1.8 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.2 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                      <ScoreBar score={item.score} />
                      <Typography sx={{ fontSize: { xs: '0.92rem', sm: '1rem' }, fontWeight: 800, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ml: 0.5 }}>
                        {item.title}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flexShrink: 0 }}>
                    {isLoading
                      ? <CircularProgress size={18} sx={{ color: TEAL }} />
                      : <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: TEAL_LIGHT, border: `1.5px solid ${TEAL_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Box component="span" sx={{ fontSize: 14, color: TEAL, lineHeight: 1 }}>›</Box>
                        </Box>
                    }
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <LanguageRoundedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                      {/* domain shown via title */}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <CalendarTodayRoundedIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>{dateStr}</Typography>
                  </Box>
                </Box>

                <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.76rem' }, color: '#444', lineHeight: 1.6 }}>
                  {item.analysisSynthesis}
                </Typography>
              </Box>

              {isLoading && (
                <LinearProgress sx={{ height: 3, '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg, ${TEAL}, #2ec4c9)` } }} />
              )}
            </Card>
          )
        })}
      </Stack>
    </Box>
  )
}


const AeoChecker = () => {
  const [view, setView] = useState('landing')
  const [isStarting, setIsStarting] = useState(false)
  const [useMockData, setUseMockData] = useState(false)
  const [startError, setStartError] = useState('')
  const [jobId, setJobId] = useState(null)
  const [progress, setProgress] = useState(0)
  const [elapsedTime, setElapsedTime] = useState(0)
  const [data, setData] = useState(null)
  const [tasksData, setTasksData] = useState(null)
  const [strengthsExpanded, setStrengthsExpanded] = useState(false)
  const [weaknessesExpanded, setWeaknessesExpanded] = useState(false)
  const [schemaMissingExpanded, setSchemaMissingExpanded] = useState(false)
  const pollIntervalRef = useRef(null)
  const elapsedIntervalRef = useRef(null)
  const { currentDomain } = useAuth()
  const [history, setHistory] = useState(null) 
  const [historyLoading, setHistoryLoading] = useState(true)
  const [isLoadingReport, setIsLoadingReport] = useState(false)
  const [selectedJobId, setSelectedJobId] = useState(null)

  useEffect(() => {
    if (!currentDomain?.domain_token) return
    setHistoryLoading(true)
    fetch(`${FAST_API_BASE_URL}/api/aeo-history`, {
      headers: { 'DOMAIN-TOKEN': currentDomain.domain_token },
    })
      .then(r => r.json())
      .then(json => {
        const list = Array.isArray(json) ? json : (json?.result || [])
        setHistory(list)
        // If no history, stay on landing
        if (list.length > 0 && view === 'landing') setView('history')
      })
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false))
  }, [currentDomain])

const handleStart = useCallback(async () => {
    if (useMockData) {
      setData(normalizeReport(MOCK_FULL_REPORT_RESPONSE.result.report))
      setTasksData(null)
      setView('results')
      return
    }
    setIsStarting(true)
    setStartError('')
    try {
      const res = await fetch(`${FAST_API_BASE_URL}/api/aeo-score`, { headers: { 'DOMAIN-TOKEN': currentDomain.domain_token } })
      if (!res.ok) throw new Error(`Server error: ${res.status}`)
      const json = await res.json()
      const id = json?.result?.job_id
      if (!id) throw new Error('No job_id returned from server')
      setJobId(id)
      setProgress(0)
      setElapsedTime(0)
      setTasksData(null)
      setView('processing')
    } catch (err) {
      setStartError(err.message || 'Failed to start audit. Please try again.')
    } finally {
      setIsStarting(false)
    }
  }, [currentDomain, useMockData])

  const handleSelectReport = useCallback(async (jobId) => {
    setIsLoadingReport(true)
    setSelectedJobId(jobId)
    try {
      const headers = { 'DOMAIN-TOKEN': currentDomain.domain_token }
      const [reportRes, tasksRes] = await Promise.all([
        fetch(`${FAST_API_BASE_URL}/api/aeo-score/report/${jobId}`, { headers }),
        fetch(`${FAST_API_BASE_URL}/api/aeo-score/report/${jobId}/tasks`, { headers }),
      ])
      const reportJson = await reportRes.json()
      const tasksJson = await tasksRes.json()
      const report = reportJson?.result?.report
      if (!report) throw new Error('No report data')
      setData(normalizeReport(report))
      const tasks = tasksJson?.result?.tasks
      if (Array.isArray(tasks)) setTasksData(tasks)
      setView('results')
    } catch (err) {
      console.error('Failed to load report:', err)
    } finally {
      setIsLoadingReport(false)
      setSelectedJobId(null)
    }
  }, [currentDomain])

useEffect(() => {
  if (view !== 'processing' || !jobId) return;

  elapsedIntervalRef.current = setInterval(() => {
    setElapsedTime(prev => prev + 1);
  }, 1000);

  const fetchTasks = async (id) => {
    try {
      const res = await fetch(
        `${FAST_API_BASE_URL}/api/aeo-score/report/${id}/tasks`,
        { headers: { 'DOMAIN-TOKEN': currentDomain.domain_token } }
      );
      if (!res.ok) return;
      const json = await res.json();
      const tasks = json?.result?.tasks;
      if (Array.isArray(tasks)) setTasksData(tasks);
    } catch (err) {
      console.error('Tasks fetch error:', err);
    }
  };

  const fetchReport = async () => {
    try {
      const res = await fetch(
        `${FAST_API_BASE_URL}/api/aeo-score/report/${jobId}`,
        { headers: { 'DOMAIN-TOKEN': currentDomain.domain_token } }
      );
      if (!res.ok) throw new Error(`Report fetch failed: ${res.status}`);
      const json = await res.json();
      const report = json?.result?.report;
      if (!report) throw new Error('No report data in response');

      setData(normalizeReport(report));
      await fetchTasks(jobId);
      setView('results');
    } catch (err) {
      console.error('Report fetch error:', err);
      setStartError('Failed to load report. Please try again.');
      setView('landing');
    }
  };

  let isCancelled = false;

  const poll = async () => {
    try {
      const res = await fetch(
        `${FAST_API_BASE_URL}/api/aeo-score/status/${jobId}`,
        { headers: { 'DOMAIN-TOKEN': currentDomain.domain_token } }
      );

      if (!res.ok) throw new Error(`Status check failed: ${res.status}`);

      const json = await res.json();
      const jobResult = json?.result;
      if (!jobResult) return;

      const { status, processed_upto, elapsed_seconds } = jobResult;

      if (processed_upto !== undefined) setProgress(processed_upto);
      if (elapsed_seconds !== undefined)
        setElapsedTime(Math.round(elapsed_seconds));

      if (status === 'COMPLETED') {
        clearInterval(elapsedIntervalRef.current);
        await fetchReport();
        return;
      }

      if (status === 'FAILED') {
        clearInterval(elapsedIntervalRef.current);
        setView('failed');
        return;
      }
      if (!isCancelled) {
        pollIntervalRef.current = setTimeout(poll, 5000);
      }
    } catch (err) {
      console.error('Polling error:', err);

      // retry after delay
      if (!isCancelled) {
        pollIntervalRef.current = setTimeout(poll, 5000);
      }
    }
  };

  poll();

  return () => {
    isCancelled = true;
    clearTimeout(pollIntervalRef.current);
    clearInterval(elapsedIntervalRef.current);
  };
}, [view, jobId]);

  const companyInfo = data?.companyInfo || {}
  const crawlerData = data?.ai_crawler_accessibility || {}
  const answerability = companyInfo.answerability || {}
  const contentQualityScore = Math.round(companyInfo.industry_product_clarity_score || 0)
  const answerabilityScore = Math.round(answerability.answerability_score || 0)
  const accessibilityScore = Math.round(crawlerData.accessibility_score || 0)
  const overallScore = data?.final_aeo_score != null ? Math.round(data.final_aeo_score) : Math.round((contentQualityScore + answerabilityScore + accessibilityScore) / 3)
  const metrics = [
    { label: 'Content Quality', value: contentQualityScore, status: getScoreStatus(contentQualityScore), note: 'Industry and product clarity score based on content analysis.' },
    { label: 'Answerability', value: answerabilityScore, status: getScoreStatus(answerabilityScore), note: `Analyzed ${answerability.totalPhrases || 0} phrases across your content.` },
    { label: 'AI Accessibility', value: accessibilityScore, status: getScoreStatus(accessibilityScore), note: `${crawlerData.ai_bot_permissions?.allowed_count || 0} AI crawlers allowed.` },
  ]
  const technicalFiles = [
    { label: 'Sitemap (sitemap.xml)', exists: crawlerData.technical_files?.sitemap_xml?.exists || false, status: crawlerData.technical_files?.sitemap_xml?.status || 'red' },
    { label: 'Robots.txt', exists: crawlerData.technical_files?.robots_txt?.exists || false, status: crawlerData.technical_files?.robots_txt?.status || 'red' },
    { label: 'llms.txt', exists: crawlerData.technical_files?.llms_txt?.exists || false, status: crawlerData.technical_files?.llms_txt?.status || 'red' },
    { label: 'Primary Language', isText: true, value: (crawlerData.language_detection?.primary_language || 'N/A').toUpperCase() },
    { label: 'Compression Enabled', exists: crawlerData.performance_analysis?.compression === 'gzip', status: crawlerData.performance_analysis?.compression ? 'green' : 'red' },
  ]
  const botDetails = crawlerData.ai_bot_permissions?.details || {}
  const vendorCrawlers = []
  Object.values(botDetails).forEach(category => {
    if (typeof category === 'object' && category !== null) {
      Object.entries(category).forEach(([botKey, status]) => {
        const mapping = VENDOR_MAPPING[botKey] || { letter: botKey[0].toUpperCase(), bgColor: '#999999', name: botKey }
        vendorCrawlers.push({ name: mapping.name, letter: mapping.letter, bgColor: mapping.bgColor, status: typeof status === 'string' ? status.toLowerCase() : 'unknown' })
      })
    }
  })
  const allowedCount = crawlerData.ai_bot_permissions?.allowed_count || 0
  const blockedCount = vendorCrawlers.filter(v => v.status === 'blocked').length
  const limitedCount = vendorCrawlers.filter(v => v.status === 'limited').length

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {view === 'landing' && !historyLoading && (
        <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: { xs: 1.5, sm: 2, md: 3 } }}>
        <LandingScreen onStart={handleStart} isStarting={isStarting} error={startError} useMockData={useMockData} onToggleMock={() => setUseMockData(v => !v)} />
        </Box>
      )}
      {historyLoading && view !== 'processing' && view !== 'results' && view !== 'failed' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
          <CircularProgress sx={{ color: TEAL }} />
        </Box>
      )}

      {view === 'history' && !historyLoading && (
        <HistoryScreen
          history={history || []}
          onSelect={handleSelectReport}
          onNewScan={handleStart}
          isLoadingReport={isLoadingReport}
          selectedJobId={selectedJobId}
        />
      )}
      {view === 'processing' && (
        <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: { xs: 1.5, sm: 2, md: 3 } }}>
          <ProcessingScreen progress={progress} elapsedTime={elapsedTime} />
        </Box>
      )}

      {view === 'failed' && (
        <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: { xs: 1.5, sm: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', px: { xs: 2, sm: 4 } }}>
            <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(229,57,53,0.08)', border: '2px solid rgba(229,57,53,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
              <ErrorOutlineRoundedIcon sx={{ fontSize: 38, color: '#E53935' }} />
            </Box>
            <Typography sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 800, color: 'text.primary', mb: 1, textAlign: 'center' }}>AEO Check Failed</Typography>
            <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: '#000000', mb: 3.5, textAlign: 'center', maxWidth: 420, lineHeight: 1.7 }}>We weren't able to run the AEO analysis on your site. Please make sure:</Typography>
            <Card sx={{ borderRadius: 3, border: '1px solid rgba(229,57,53,0.18)', bgcolor: 'rgba(229,57,53,0.03)', mb: 3.5, width: '100%', maxWidth: 440, boxShadow: '0 4px 20px rgba(229,57,53,0.08)' }}>
              {[
                { icon: <LanguageRoundedIcon />, text: 'Your domain or site is live and publicly accessible' },
                { icon: <LockRoundedIcon />, text: 'The site is not behind a login wall or firewall' },
                { icon: <TrackChangesRoundedIcon />, text: 'Your robots.txt is not blocking all crawlers' },
                { icon: <SpeedRoundedIcon />, text: 'The site responds without timeouts' },
              ].map(({ icon, text }, idx, arr) => (
                <Box key={idx}>
                  <Box sx={{ px: 2.5, py: 1.6, display: 'flex', alignItems: 'center', gap: 1.8 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, bgcolor: 'rgba(229,57,53,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {React.cloneElement(icon, { sx: { fontSize: 16, color: '#E53935' } })}
                    </Box>
                    <Typography sx={{ fontSize: { xs: '0.78rem', sm: '0.83rem' }, color: '#000000', lineHeight: 1.55 }}>{text}</Typography>
                  </Box>
                  {idx < arr.length - 1 && <Divider sx={{ borderColor: 'rgba(229,57,53,0.10)', mx: 2.5 }} />}
                </Box>
              ))}
            </Card>
            <Button variant="contained" size="large" onClick={() => { setView('landing'); setJobId(null); setProgress(0); setElapsedTime(0) }}
              startIcon={<SearchRoundedIcon />}
              sx={{ px: 4, py: 1.5, borderRadius: 3, fontSize: { xs: '0.88rem', sm: '0.95rem' }, fontWeight: 700, background: `linear-gradient(135deg, ${TEAL}, #2ec4c9)`, boxShadow: `0 8px 24px rgba(33,146,151,0.3)`, textTransform: 'none', color: '#fff', transition: 'all 0.2s', '&:hover': { background: `linear-gradient(135deg, #1a7a7e, ${TEAL})`, transform: 'translateY(-2px)', boxShadow: `0 12px 28px rgba(33,146,151,0.38)` } }}>
              Try Again
            </Button>
          </Box>
        </Box>
      )}

      {view === 'results' && data && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: { lg: 'calc(100vh - 64px)' }, overflow: 'hidden', px: { xs: 1.5, sm: 2, md: 3 }, pt: { xs: 1.5, sm: 2, md: 3 } }}>
          <Box sx={{ flexShrink: 0, mb: { xs: 2, sm: 3 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: { xs: '1.15rem', sm: '1.35rem', md: '1.5rem' }, fontWeight: 700, mb: 0.4 }}>AEO Checker</Typography>
              <Typography sx={{ fontSize: { xs: '0.78rem', sm: '0.875rem' }, color: '#000000' }}>Answer Engine Optimization — Complete Analysis</Typography>
            </Box>
            <Button variant="outlined" size="small" onClick={() => { setData(null); setTasksData(null); setView(history && history.length > 0 ? 'history' : 'landing') }}
              startIcon={<SearchRoundedIcon />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderColor: TEAL_BORDER, color: TEAL, '&:hover': { borderColor: TEAL, bgcolor: TEAL_LIGHT } }}>
              Check score again
            </Button>
          </Box>

          <Box sx={{ display: { xs: 'block', lg: 'flex' }, gap: 2.5, flex: 1, minHeight: 0, pb: { xs: 3, lg: 0 } }}>

            <Box sx={{ width: { lg: 320, xl: 360 }, flexShrink: 0, mb: { xs: 2.5, lg: 0 } }}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid rgba(0,0,0,0.05)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ height: 5, background: `linear-gradient(90deg, ${TEAL}, #2ec4c9)`, flexShrink: 0 }} />
                <Box sx={{ p: { xs: 2, sm: 3 }, flex: 1, overflowY: { lg: 'auto' }, '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.15)', borderRadius: 10 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: 2.5 }}>
                    <ScoreRing score={overallScore} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }, fontWeight: 700, color: 'text.primary', lineHeight: 1.4, wordBreak: 'break-word' }}>
                        {companyInfo.name?.split('(')[0].trim() || 'Company Name'}
                      </Typography>
                      <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, color: 'text.disabled' }}>Overall AEO Score</Typography>
                    </Box>
                  </Box>

                  <Stack spacing={1.2} mb={2.5}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
                      <CalendarTodayRoundedIcon sx={{ fontSize: 15, color: 'text.disabled', mt: 0.2, flexShrink: 0 }} />
                      <Box>
                        <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, color: 'text.disabled', display: 'block', lineHeight: 1, mb: 0.1, fontWeight: 600 }}>Scanned</Typography>
                        <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.78rem' }, color: 'text.primary', wordBreak: 'break-word' }}>
                          {new Date().toLocaleDateString()} · {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
                      <LanguageRoundedIcon sx={{ fontSize: 15, color: 'text.disabled', mt: 0.2, flexShrink: 0 }} />
                      <Box>
                        <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, color: 'text.disabled', display: 'block', lineHeight: 1, mb: 0.1, fontWeight: 600 }}>Location</Typography>
                        <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.78rem' }, color: 'text.primary', wordBreak: 'break-word' }}>
                          {Array.isArray(companyInfo.location) ? companyInfo.location[0]?.split('(')[0].trim() : companyInfo.location?.split('(')[0].trim() || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                  <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 1 }}>Industry</Typography>
                    <Chip size="small" label={companyInfo.industry?.split('(')[0].trim() || 'N/A'} sx={{ bgcolor: TEAL_LIGHT, color: TEAL, fontWeight: 600, fontSize: { xs: '0.68rem', sm: '0.72rem' }, border: `1px solid ${TEAL_BORDER}`, height: 'auto', '& .MuiChip-label': { py: 0.5, whiteSpace: 'normal', wordBreak: 'break-word' } }} />
                  </Box>
                  <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 1 }}>Description</Typography>
                    <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.78rem' }, color: '#000000', lineHeight: 1.6, display: 'block', wordBreak: 'break-word' }}>
                      {companyInfo.description || 'No description available'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 1 }}>Key Initiatives</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
                      {(companyInfo.keyProducts || []).slice(0, 4).map((product, idx) => (
                        <Chip key={idx} size="small" label={product.split('(')[0].trim()} variant="outlined" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, borderColor: 'rgba(0,0,0,0.1)', color: '#000000', height: 'auto', '& .MuiChip-label': { py: 0.4, whiteSpace: 'normal', wordBreak: 'break-word' } }} />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Box>

            <Box sx={{ flex: 1, overflowY: { lg: 'auto' }, minHeight: 0, pr: { lg: 0.5 }, pb: { xs: 0, lg: 3 }, '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-track': { bgcolor: 'transparent' }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.15)', borderRadius: 10 }, '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'rgba(0,0,0,0.28)' } }}>
              <Stack spacing={2.5}>

                <Stack spacing={1.5}>
                  <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 700, color: '#000000', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                    Analysis Breakdown
                  </Typography>
                  {metrics.map(({ label, value, status, note }) => {
                    const { bg, border, text } = diagStatusColor(status)
                    return (
                      <Card key={label} sx={{ borderRadius: 2.5, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: `1px solid ${border}`, bgcolor: bg, transition: 'transform 0.15s, box-shadow 0.15s', '&:hover': { transform: 'translateX(3px)', boxShadow: '0 4px 18px rgba(0,0,0,0.09)' } }}>
                        <Box sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                          <DiagStatusIcon status={status} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.3, gap: 1 }}>
                              <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 700, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Typography>
                              <Typography sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 800, color: text, flexShrink: 0 }}>{value}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, color: '#000000', wordBreak: 'break-word' }}>{note}</Typography>
                          </Box>
                        </Box>
                      </Card>
                    )
                  })}
                </Stack>

                <ActionPlanSection tasks={tasksData} section="COMPANY_INFO" />

                <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                  <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2.5 }, pb: 1.5 }}>
                    <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1rem', md: '1.0625rem' }, fontWeight: 700, color: 'text.primary', mb: 0.4 }}>Content Answerability Analysis</Typography>
                    <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.78rem' }, color: '#000000', lineHeight: 1.6 }}>Detailed breakdown of content structure and AI-friendliness.</Typography>
                  </Box>
                  <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
                  <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                    <Grid container spacing={{ xs: 1, sm: 2 }}>
                      {[
                        { icon: <ArticleRoundedIcon sx={{ fontSize: { xs: 15, sm: 18 }, color: TEAL }} />, label: 'Total Phrases', value: answerability.totalPhrases || 0 },
                        { icon: <CheckCircleRoundedIcon sx={{ fontSize: { xs: 15, sm: 18 }, color: TEAL }} />, label: 'Good Length', value: answerability.isGoodLengthPhrase || 0 },
                        { icon: <ChatBubbleRoundedIcon sx={{ fontSize: { xs: 15, sm: 18 }, color: TEAL }} />, label: 'Conversational', value: answerability.isConversationalPhrase || 0 },
                        { icon: <BarChartRoundedIcon sx={{ fontSize: { xs: 15, sm: 18 }, color: TEAL }} />, label: 'With Statistics', value: answerability.hasStatisticsPhrase || 0 },
                        { icon: <FormatQuoteRoundedIcon sx={{ fontSize: { xs: 15, sm: 18 }, color: TEAL }} />, label: 'With Citations', value: answerability.hasCitationPhrase || 0 },
                        { icon: answerability.hasCitationsSection ? <CheckCircleRoundedIcon sx={{ fontSize: { xs: 15, sm: 18 }, color: '#43A047' }} /> : <CancelRoundedIcon sx={{ fontSize: { xs: 15, sm: 18 }, color: '#E53935' }} />, label: 'Citations Section', value: answerability.hasCitationsSection ? 'Yes' : 'No', color: answerability.hasCitationsSection ? '#43A047' : '#E53935' },
                      ].map((stat, idx) => (
                        <Grid item xs={6} sm={4} md={4} lg={4} key={idx}>
                          <StatCard icon={stat.icon} label={stat.label} value={stat.value} color={stat.color || TEAL} />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                  <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
                  <Box>
                    <Box onClick={() => setStrengthsExpanded(!strengthsExpanded)} sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', bgcolor: '#F0FDF4', '&:hover': { bgcolor: '#DCFCE7' }, transition: 'background 0.2s' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUpIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#43A047', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 700, color: '#43A047' }}>Strengths ({answerability.strengths?.length || 0})</Typography>
                      </Box>
                      {strengthsExpanded ? <ExpandLessIcon sx={{ fontSize: { xs: 18, sm: 24 } }} /> : <ExpandMoreIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />}
                    </Box>
                    <Collapse in={strengthsExpanded}>
                      <List sx={{ py: 0 }}>
                        {(answerability.strengths || []).map((s, idx) => (
                          <ListItem key={idx} sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 2, sm: 2.5 }, borderTop: idx > 0 ? '1px solid rgba(0,0,0,0.05)' : 'none', alignItems: 'flex-start' }}>
                            <ListItemIcon sx={{ minWidth: { xs: 28, sm: 32 }, mt: 0.2 }}><CheckCircleRoundedIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: '#43A047' }} /></ListItemIcon>
                            <ListItemText primary={s} primaryTypographyProps={{ sx: { fontSize: { xs: '0.72rem', sm: '0.78rem' }, color: '#000000', lineHeight: 1.6, wordBreak: 'break-word' } }} />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                  <Box>
                    <Box onClick={() => setWeaknessesExpanded(!weaknessesExpanded)} sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', bgcolor: '#FEF2F2', '&:hover': { bgcolor: '#FEE2E2' }, transition: 'background 0.2s' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingDownIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#E53935', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 700, color: '#E53935' }}>Areas for Improvement ({answerability.weaknesses?.length || 0})</Typography>
                      </Box>
                      {weaknessesExpanded ? <ExpandLessIcon sx={{ fontSize: { xs: 18, sm: 24 } }} /> : <ExpandMoreIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />}
                    </Box>
                    <Collapse in={weaknessesExpanded}>
                      <List sx={{ py: 0 }}>
                        {(answerability.weaknesses || []).map((w, idx) => (
                          <ListItem key={idx} sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 2, sm: 2.5 }, borderTop: idx > 0 ? '1px solid rgba(0,0,0,0.05)' : 'none', alignItems: 'flex-start' }}>
                            <ListItemIcon sx={{ minWidth: { xs: 28, sm: 32 }, mt: 0.2 }}><WarningAmberRoundedIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: '#FB8C00' }} /></ListItemIcon>
                            <ListItemText primary={w} primaryTypographyProps={{ sx: { fontSize: { xs: '0.72rem', sm: '0.78rem' }, color: '#000000', lineHeight: 1.6, wordBreak: 'break-word' } }} />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                </Card>

                <ActionPlanSection tasks={tasksData} section="ANSWERABILITY" />

                <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                  <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2.5 }, pb: 1.5 }}>
                    <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1rem', md: '1.0625rem' }, fontWeight: 700, color: 'text.primary', mb: 0.4 }}>AI Crawler Readiness</Typography>
                    <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.78rem' }, color: '#000000', lineHeight: 1.6 }}>Snapshot of robots.txt access by major AI and search crawlers.</Typography>
                  </Box>
                  <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 1, sm: 2 }, p: { xs: 1.5, sm: 2.5 }, bgcolor: '#FAFBFC' }}>
                    {[
                      { label: 'Allowed', value: allowedCount, color: '#43A047' },
                      { label: 'Limited', value: limitedCount, color: '#FB8C00' },
                      { label: 'Blocked', value: blockedCount, color: '#E53935' },
                    ].map(({ label, value, color }) => (
                      <Box key={label}>
                        <Typography sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' }, color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 0.5 }}>{label}</Typography>
                        <Typography sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' }, fontWeight: 800, color, lineHeight: 1 }}>{value}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
                  <Box sx={{ px: { xs: 1.5, sm: 2.5 }, py: 2 }}>
                    <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 700, color: '#000000', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', mb: 2 }}>
                      Vendor Breakdown ({vendorCrawlers.length} Bots)
                    </Typography>
                    {Object.entries(botDetails).map(([categoryKey, categoryBots], catIdx) => (
                      <BotCategory key={categoryKey} categoryKey={categoryKey} categoryBots={categoryBots} catIdx={catIdx} />
                    ))}
                  </Box>
                </Card>

                <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                  <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2.5 }, pb: 1.5 }}>
                    <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1rem', md: '1.0625rem' }, fontWeight: 700, color: 'text.primary', mb: 0.4 }}>AI Crawler Accessibility</Typography>
                    <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.78rem' }, color: '#000000', lineHeight: 1.6 }}>Does your website have well structured semantics, use correct schema markup, and is easily accessible for AI crawlers?</Typography>
                  </Box>
                  <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
                  <Box>
                    {technicalFiles.map(({ label, exists, status, isText, value }, idx) => {
                      if (label === 'llms.txt') {
                        return <LlmsTxtRow key={label} exists={exists} status={status} idx={idx} totalLen={technicalFiles.length} currentDomain={currentDomain} />
                      }
                      return (
                        <Box key={label}>
                          <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1.2, sm: 1.6 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, bgcolor: idx % 2 === 0 ? '#fff' : '#FAFBFC', transition: 'background 0.15s', '&:hover': { bgcolor: TEAL_LIGHT } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.8, sm: 1.2 }, minWidth: 0, flex: 1 }}>
                              <FiberManualRecordRoundedIcon sx={{ fontSize: 7, color: 'text.disabled', flexShrink: 0 }} />
                              <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Typography>
                            </Box>
                            {isText
                              ? <Chip label={value} size="small" sx={{ bgcolor: TEAL_LIGHT, color: TEAL, fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.72rem' }, border: `1px solid ${TEAL_BORDER}`, minWidth: 36, flexShrink: 0 }} />
                              : <CrawlerStatus exists={exists} status={status} />
                            }
                          </Box>
                          {idx < technicalFiles.length - 1 && <Divider sx={{ borderColor: 'rgba(0,0,0,0.05)', mx: { xs: 2, sm: 2.5 } }} />}
                        </Box>
                      )
                    })}
                  </Box>
                </Card>

                <ActionPlanSection tasks={tasksData} section="TECHNICAL" />

                {/* Structured Data */}
                <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                  <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2.5 }, pb: 1.5 }}>
                    <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1rem', md: '1.0625rem' }, fontWeight: 700, color: 'text.primary', mb: 0.4 }}>Structured Data</Typography>
                    <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.78rem' }, color: '#000000', lineHeight: 1.6 }}>Does your website use correct semantic tags, HTML elements, use schemas, and overall is well structured for AI chatbots?</Typography>
                  </Box>
                  <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
                  {(() => {
                    const structureData = data?.structure_data_details || {}
                    const schemaDetails = structureData.schema_details?.schema_markup_details || {}
                    const semanticData = structureData.semantic_details?.semantic_elements || {}
                    return (
                      <>
                        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1.4, sm: 1.8 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fff', '&:hover': { bgcolor: TEAL_LIGHT }, transition: 'background 0.15s' }}>
                          <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 700, color: 'text.primary' }}>Schema Markup</Typography>
                          <CrawlerStatus exists={schemaDetails.schema_markup_present} status={schemaDetails.schema_markup_present ? 'green' : 'red'} />
                        </Box>
                        <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
                        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1.4, sm: 1.8 }, bgcolor: '#fff', '&:hover': { bgcolor: TEAL_LIGHT }, transition: 'background 0.15s' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 700, color: 'text.primary', flexShrink: 0 }}>Semantic Elements</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {(semanticData.unique_types_found || []).map((tag, idx) => (
                                <Chip key={idx} label={`<${tag}>`} size="small" sx={{ bgcolor: '#374151', color: '#fff', fontWeight: 600, fontSize: { xs: '0.6rem', sm: '0.65rem' }, borderRadius: '6px', height: 22, '& .MuiChip-label': { px: 0.8 } }} />
                              ))}
                            </Box>
                          </Box>
                        </Box>
                        <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', bgcolor: '#FAFBFC' }}>
                          {[
                            { label: 'Tags Found', value: semanticData.all_tags_count || 0 },
                            { label: 'Semantic Elements', value: semanticData.semantic_tags_count || 0 },
                            { label: 'Non-Semantics', value: semanticData.non_semantic_tags_count || 0 },
                          ].map(({ label, value }, idx) => (
                            <Box key={label} sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 2 }, borderRight: idx < 2 ? '1px solid rgba(0,0,0,0.06)' : 'none', '&:hover': { bgcolor: TEAL_LIGHT }, transition: 'background 0.15s' }}>
                              <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, color: '#000000', fontWeight: 600, mb: 0.5, display: 'block' }}>{label}</Typography>
                              <Typography sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 800, color: 'text.primary', lineHeight: 1 }}>{value.toLocaleString()}</Typography>
                            </Box>
                          ))}
                        </Box>
                        {(schemaDetails.schema_markup_missing_pages || []).length > 0 && (
                          <>
                            <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
                            <Box>
                              <Box onClick={() => setSchemaMissingExpanded(!schemaMissingExpanded)} sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', bgcolor: '#FEF2F2', '&:hover': { bgcolor: '#FEE2E2' }, transition: 'background 0.2s' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <ErrorOutlineRoundedIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#E53935', flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 700, color: '#E53935' }}>
                                    Pages Missing Schema ({schemaDetails.pages_without_schema_markups})
                                  </Typography>
                                </Box>
                                {schemaMissingExpanded ? <ExpandLessIcon sx={{ fontSize: { xs: 18, sm: 24 }, flexShrink: 0 }} /> : <ExpandMoreIcon sx={{ fontSize: { xs: 18, sm: 24 }, flexShrink: 0 }} />}
                              </Box>
                              <Collapse in={schemaMissingExpanded}>
                                <List sx={{ py: 0 }}>
                                  {schemaDetails.schema_markup_missing_pages.map((page, pageIdx) => (
                                    <JsonLdRow key={pageIdx} page={page} pageIdx={pageIdx} totalPages={schemaDetails.schema_markup_missing_pages.length} />
                                  ))}
                                </List>
                              </Collapse>
                            </Box>
                          </>
                        )}
                      </>
                    )
                  })()}
                </Card>

  
                <ActionPlanSection tasks={tasksData} section="STRUCTURE" />

                <WebPresenceCard webPresenceData={data?.web_presence} />

                <ActionPlanSection tasks={tasksData} section="WEB_PRESENCE" />

                {data?.ai_presence_report?.length > 0 ? (
                    <AiPresenceCard aiPresenceReport={data.ai_presence_report} />
                  ) : (
                    <ComingSoonWrapper label="AI Presence — Coming Soon">
                      <AiPresenceCard aiPresenceReport={[
                        { prompt: "What are best practices for cloud migration?", model: "CHATGPT", target_domain_found: false, competitors: ["aws.com", "azure.com", "google.com"], response: "Sample response..." },
                        { prompt: "Which firms lead in AI transformation?", model: "GEMINI", target_domain_found: true, competitors: ["accenture.com", "deloitte.com"], response: "Sample response..." },
                      ]} />
                    </ComingSoonWrapper>
                  )}
                {/* 
                <ComingSoonWrapper label="Competitor Landscape — Coming Soon">
                  <CompetitorLandscapeCard competitorData={{ score: 0, result: { openai: { gpt_4oMini: { competitors: ['Accenture', 'McKinsey', 'Deloitte', 'PwC', 'BCG'], included: false, score: 0 }, score: 0 }, gemini: { gemini_2_0Flash: { competitors: ['IBM', 'Capgemini', 'Infosys', 'TCS', 'Wipro'], included: false, score: 0 }, score: 0 } } }} />
                </ComingSoonWrapper> */}

              </Stack>
            </Box>
          </Box>
        </Box>
      )}
    </DashboardLayout>
  )
}

export default AeoChecker